import React, { useState, useEffect } from "react";

import { toast } from "react-toastify";

import axiosInstance from "../config/axios";

import { formatDate } from "../utils/eftUtils";
import { roundTo2 } from "../utils/numberUtils";
import { DEFAULT_TAX_RATE } from "../constants/tax";
import { officeExpensesSplitForAgent } from "../utils/officeExpenses";

const paymentTypes = ["EFT", "Cheque", "Wire", "Cash"];

const ACCOUNT_12100_NAME = "A/R - Agt's Recoverable- Gen Exp";
const ACCOUNT_12101_NAME = "A/R - Agt's Recoverable - Commission";

/**
 * Split "Tax For Deduction" (= totalFees − feesDeducted) on finalize.
 * Credits must sum to that amount only (never uncapped OE balances):
 * - Plan fee HST → Credit 23001
 * - Remainder: commission deduction (incl. tax) → Credit 12101
 * - Rest of remainder (desk/courier/other OE incl. tax) → Credit 12100
 */
function buildAgentFeeTaxCreditLines(
  agent,
  { description, date, taxRate = DEFAULT_TAX_RATE } = {},
) {
  const feesDeducted = roundTo2(parseFloat(agent?.feesDeducted || 0) || 0);
  const totalFees = roundTo2(parseFloat(agent?.totalFees || 0) || 0);
  const taxForDeduction = roundTo2(Math.max(0, totalFees - feesDeducted));
  if (taxForDeduction <= 0) return [];

  const planTax = roundTo2(
    Math.min(feesDeducted * taxRate, taxForDeduction),
  );
  let remaining = roundTo2(Math.max(0, taxForDeduction - planTax));

  const split = officeExpensesSplitForAgent(agent, taxRate);
  const desired12100 = roundTo2(split.officeExpensesAmount || 0);
  const desired12101 = roundTo2(split.commissionDeductionAmount || 0);
  const oePool = roundTo2(desired12100 + desired12101);

  let to12101 = 0;
  let to12100 = 0;
  if (remaining > 0.005) {
    if (oePool > 0.005) {
      // Prefer stored split, but scale down so we never exceed Tax For Deduction.
      if (oePool <= remaining + 0.005) {
        to12101 = desired12101;
        to12100 = roundTo2(remaining - to12101);
      } else {
        to12101 = roundTo2(remaining * (desired12101 / oePool));
        to12100 = roundTo2(remaining - to12101);
      }
    } else {
      // No line-item OE — entire non-plan portion is recoverable gen exp.
      to12100 = remaining;
    }
  }

  // Absorb penny drift so credits exactly equal Tax For Deduction.
  const sum = roundTo2(planTax + to12100 + to12101);
  const drift = roundTo2(taxForDeduction - sum);
  if (Math.abs(drift) >= 0.005) {
    if (to12100 > 0 || to12101 <= 0) to12100 = roundTo2(to12100 + drift);
    else to12101 = roundTo2(to12101 + drift);
  }

  const lines = [];
  if (planTax > 0.005) {
    lines.push({
      accountNumber: "23001",
      accountName: "HST Input Tax Credit",
      debit: 0,
      credit: planTax,
      description,
      date,
    });
  }
  if (to12100 > 0.005) {
    lines.push({
      accountNumber: "12100",
      accountName: ACCOUNT_12100_NAME,
      debit: 0,
      credit: to12100,
      description,
      date,
    });
  }
  if (to12101 > 0.005) {
    lines.push({
      accountNumber: "12101",
      accountName: ACCOUNT_12101_NAME,
      debit: 0,
      credit: to12101,
      description,
      date,
    });
  }
  return lines;
}

async function postAgentFeeTaxCreditLines(agent, opts) {
  const lines = buildAgentFeeTaxCreditLines(agent, opts);
  for (const line of lines) {
    await axiosInstance.post("/ledger", line);
  }
  return lines;
}

/**
 * Ledger lines that belong under Commission Trust / transfer flows, not the main
 * trade finalization journal on the printout.
 * - Agent commission EFT: Dr 21500 / Cr 10004 (description includes "Commission Trust")
 * - RE Trust → Commission Trust transfer: Dr 10004 / Cr 10002 (commission-transfer API)
 */
function isCommissionTrustOffJournalLedgerRow(row) {
  if (!row?.description || !/commission trust/i.test(row.description))
    return false;
  const cr = parseFloat(row.credit) || 0;
  const dr = parseFloat(row.debit) || 0;
  return (
    (row.accountNumber === "10004" && cr > 0) ||
    (row.accountNumber === "21500" && dr > 0) ||
    (row.accountNumber === "10004" && dr > 0) ||
    (row.accountNumber === "10002" && cr > 0)
  );
}

const ledgerRowMatchesTradeTag = (row, tradeNumber) => {
  const tn = String(tradeNumber ?? "").trim();
  if (!tn) return false;
  const desc = String(row?.description || "");
  if (desc.includes(`Trade #: ${tn}`) || desc.includes(`Trade #:${tn}`)) {
    return true;
  }
  const escaped = tn.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`Trade\\s*#:?\\s*${escaped}\\b`, "i").test(desc);
};

const rowBelongsToTrade = (row, trade) => {
  const tn = String(
    trade?.tradeNumber ?? trade?.keyInfo?.tradeNumber ?? "",
  ).trim();
  return ledgerRowMatchesTradeTag(row, tn);
};

/** Commission trust EFT reversals from EFT Reversal tab (REV-####, EFT reversal (commission)). */
function isCommissionEftReversalLedgerRow(row, tradeNumber) {
  if (!ledgerRowMatchesTradeTag(row, tradeNumber)) return false;
  const eft = String(row?.eftNumber ?? "").trim();
  const desc = String(row?.description || "");
  if (/^REV-\d+$/i.test(eft)) return true;
  return /eft\s+reversal\s*\(commission\)/i.test(desc);
}

/** Mirror of commission-trust EFT activity (already listed under Commission Reversals). */
function isCommissionTrustPaymentMirrorOnBatchReversal(row) {
  const desc = String(row?.description || "");
  if (!/received\s+from/i.test(desc) && !/paid\s+to/i.test(desc)) {
    return false;
  }
  if (/commission\s+trust/i.test(desc)) return true;
  return isCommissionTrustOffJournalLedgerRow(row);
}

/** Finalize / deposit-received batch reversals (REV#trade, REV-FIN-*). */
function isFinalizeBatchReversalLedgerRow(row, tradeNumber) {
  const eft = String(row?.eftNumber ?? "").trim();
  if (!eft || !/^REV/i.test(eft)) return false;
  if (isCommissionEftReversalLedgerRow(row, tradeNumber)) return false;
  if (isCommissionTrustPaymentMirrorOnBatchReversal(row)) return false;
  if (!ledgerRowMatchesTradeTag(row, tradeNumber)) return false;
  const tn = String(tradeNumber ?? "").replace(/\s+/g, "");
  if (/^REV#/i.test(eft)) {
    return eft.toUpperCase() === `REV#${tn}`.toUpperCase();
  }
  if (/^REV-FIN-/i.test(eft)) {
    return eft.toUpperCase().includes(tn.toUpperCase());
  }
  const desc = String(row?.description || "");
  if (/deposit\s+received\s+reversal/i.test(desc)) return true;
  return /^REV$/i.test(eft);
}

function isReversalLedgerRow(row, tradeNumber) {
  return (
    isFinalizeBatchReversalLedgerRow(row, tradeNumber) ||
    isCommissionEftReversalLedgerRow(row, tradeNumber)
  );
}

/** Builder repayment, general→commission transfer, commission recall — separate print table. */
function isPostFinalizeActivityLedgerRow(row, tradeNumber) {
  const desc = String(row?.description || "");
  const tradeTag = `Trade #: ${tradeNumber}`;
  if (!desc.includes(tradeTag) && !desc.includes(`Trade #:${tradeNumber}`)) {
    return false;
  }
  return (
    /builder\s+repayment/i.test(desc) ||
    /commission\s+transfer\s+from\s+general/i.test(desc) ||
    /general\s+to\s+commission/i.test(desc) ||
    /commission\s+recalled/i.test(desc)
  );
}

/** Same deposit posted twice (e.g. trust form re-apply with new date) — show one row per logical line */
function dedupeTrustDepositLedgerRows(rows) {
  const key = (r) =>
    [
      r.accountNumber,
      r.debit || 0,
      r.credit || 0,
      (r.description || "").trim().toLowerCase(),
    ].join("|");
  const best = new Map();
  for (const r of rows) {
    const k = key(r);
    const prev = best.get(k);
    const d = String(r.date || "");
    if (!prev || d > String(prev.date || "")) best.set(k, r);
  }
  return Array.from(best.values()).sort((a, b) =>
    String(a.date || "").localeCompare(String(b.date || "")),
  );
}

const TradeFinalizeModal = ({ trade, onClose }) => {
  // Set finalizedDate to current date by default - use local date without timezone conversion

  const today = new Date();

  const todayStr = `${today.getFullYear()}-${String(
    today.getMonth() + 1,
  ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  function formatDateToMMDDYYYY(dateStr) {
    if (!dateStr) return "";

    const [yyyy, mm, dd] = dateStr.split("-");

    return `${mm}/${dd}/${yyyy}`;
  }

  const [finalizedDate, setFinalizedDate] = useState(todayStr);

  const [closingDate, setClosingDate] = useState(
    trade?.keyInfo?.closeDate || "",
  );

  const [end, setEnd] = useState("");

  const [isFinalizing, setIsFinalizing] = useState(false);

  // New state for payment popup

  const [showPaymentPopup, setShowPaymentPopup] = useState(false);

  const [receivedFrom, setReceivedFrom] = useState("");

  const [paymentAmount, setPaymentAmount] = useState("");

  const [paymentType, setPaymentType] = useState(paymentTypes[0]);

  const [eftNumber, setEftNumber] = useState("");

  const [commissionTrustTransactions, setCommissionTrustTransactions] =
    useState([]);

  const [realEstateTrustTransactions, setRealEstateTrustTransactions] =
    useState([]);

  const [ledgerEntries, setLedgerEntries] = useState([]);

  const [fallenThru, setFallenThru] = useState(false);

  useEffect(() => {
    if (trade?._id) {
      axiosInstance

        .get(`/commission-trust-eft/trade/${trade._id}`)

        .then((res) => setCommissionTrustTransactions(res.data))

        .catch(() => setCommissionTrustTransactions([]));

      axiosInstance

        .get(`/real-estate-trust-eft/trade/${trade._id}`)

        .then((res) => setRealEstateTrustTransactions(res.data))

        .catch(() => setRealEstateTrustTransactions([]));

      axiosInstance

        .get(`/ledger?tradeId=${trade._id}`)

        .then((res) => setLedgerEntries(res.data))

        .catch(() => setLedgerEntries([]));
    }
  }, [trade?._id]);

  // Auto-set end value based on We Hold value

  useEffect(() => {
    if (getWeHoldValue() === "No") {
      setEnd("Selling Side");
    }
  }, [trade]);

  // Reset EFT number when trade changes

  useEffect(() => {
    setEftNumber("");
  }, [trade?._id]);

  // When Confirm Payment popup opens and EFT# is needed, ensure we have a value to show (fallback if API hasn't set it yet)
  useEffect(() => {
    if (
      showPaymentPopup &&
      (parseFloat(getDepositAmount()) || 0) <
        (parseFloat(getTotalCommission()) || 0) &&
      !eftNumber
    ) {
      const local = generateLocalEFTNumber();
      if (local != null && local !== "") {
        setEftNumber(String(local));
      }
    }
  }, [showPaymentPopup, eftNumber]);

  // Helper functions to extract required fields

  const getDepositAmount = () => {
    // From trust form under trade info — sum all deposit entries

    if (trade?.trustRecords && trade.trustRecords.length > 0) {
      const total = trade.trustRecords.reduce((sum, record) => {
        const amount = parseFloat(
          record.amount || record.depositAmount || record.total || 0,
        );
        return sum + (Number.isFinite(amount) ? amount : 0);
      }, 0);
      return total.toFixed(2);
    }

    return "-";
  };

  // Helper to get 'We Hold' value from trust form

  const getWeHoldValue = () => {
    if (trade?.trustRecords && trade.trustRecords.length > 0) {
      return trade.trustRecords[0].weHold || "-";
    }

    return "-";
  };

  const getCommissionData = () => {
    // From commission income section under commission form

    const rows = trade?.commission?.commissionIncomeRows || [];

    // For each row, check if it is for Listing Side or Selling Side by 'end' or fallback to first row

    // If not present, fallback to first row for backward compatibility

    let listing = rows.find((r) => r.end === "Listing Side");

    let selling = rows.find((r) => r.end === "Selling Side");

    // If not found, try to use the first row for both

    if (!listing && rows.length > 0) listing = rows[0];

    if (!selling && rows.length > 0) selling = rows[0];

    return { listing, selling };
  };

  const getAgentCommissionData = () => {
    // From agent info section under trade info

    const agent = (trade?.agentCommissionList || [])[0] || {};

    return agent;
  };

  const getAgentCommissionDataList = () => {
    // From agent info section under trade info - return all agents

    return trade?.agentCommissionList || [];
  };

  const commissionData = getCommissionData();

  const agentData = getAgentCommissionData();

  const agentList = getAgentCommissionDataList();

  // Helper functions to get individual agent data

  const getAgent1Data = () => {
    return agentList[0] || {};
  };

  const getAgent2Data = () => {
    return agentList[1] || {};
  };

  // Group commission rows by agent (agentId or agentName), each with optional listing/selling row
  const getAgentsWithListingSellingDetails = () => {
    const list = trade?.agentCommissionList || [];
    const byAgent = new Map();
    const agentOrder = [];
    list.forEach((row) => {
      const key =
        row.agentId != null && row.agentId !== ""
          ? String(row.agentId)
          : (row.agentName || "").trim() || `idx-${byAgent.size}`;
      if (!byAgent.has(key)) {
        const group = {
          agentName: row.agentName || "Agent",
          listingRow: null,
          sellingRow: null,
          _count: 0,
        };
        byAgent.set(key, group);
        agentOrder.push(group);
      }
      const group = byAgent.get(key);
      const txType = (row.transactionType || "").toUpperCase();
      if (txType === "LISTING") {
        group.listingRow = row;
      } else if (txType === "SELLING") {
        group.sellingRow = row;
      } else {
        group._count += 1;
        if (group._count === 1) {
          group.listingRow = row;
        } else {
          group.sellingRow = row;
        }
      }
    });
    return agentOrder.map(({ agentName, listingRow, sellingRow }) => ({
      agentName,
      listingRow,
      sellingRow,
    }));
  };

  // Helper to check if we should show dual agent fields (2+ commission rows = listing + selling sides)

  const shouldShowDualAgentFields = () => {
    const agentCount = agentList.length;

    return agentCount >= 2;
  };

  // Only show Listing/Selling side layout when trade classification is Agent Double Ender
  const isAgentDoubleEnder = () =>
    (trade?.keyInfo?.classification || "").toUpperCase() ===
    "AGENT DOUBLE ENDER";

  const showDualSideLayout = () =>
    isAgentDoubleEnder() && shouldShowDualAgentFields();

  /** Agent Double Ender + at least two agents (grouped listing/selling rows). */
  const shouldUseAgentDoubleEnderMultiAgentPostings = () => {
    if (!isAgentDoubleEnder()) return false;
    return getAgentsWithListingSellingDetails().length >= 2;
  };

  /**
   * Ledger sequence for Agent Double Ender with 2+ agents (grouped listing/selling rows).
   * Agent 1: 50100 listing, 21500×2, listing tax, four fee lines. Each further agent:
   * 44100/23001 listing fees, 21500×2 nets. Then deposit, selling tax, income, HST;
   * if commission > deposit, 12200 Dr (shortfall) on finalized date; 10004 Dr / 12200 Cr on Confirm Payment.
   * Then 21300 selling; then each agent after first gets selling-side 44100/23001 fee lines.
   */
  const buildAgentDoubleEnderMultiAgentLedgerRows = ({
    commissionDescription,
    commissionDate,
    listingAmount,
    sellingAmount,
    listingTax,
    sellingTax,
    depositAmount,
  }) => {
    const groups = getAgentsWithListingSellingDetails();
    const n = groups.length;
    if (n < 2) return [];

    const l0 = groups[0]?.listingRow || {};
    const s0 = groups[0]?.sellingRow || {};

    const la = roundTo2(parseFloat(listingAmount) || 0);
    const sa = roundTo2(parseFloat(sellingAmount) || 0);
    const lt = roundTo2(parseFloat(listingTax) || 0);
    const st = roundTo2(parseFloat(sellingTax) || 0);
    const dep = roundTo2(parseFloat(depositAmount) || 0);
    const totalGross = roundTo2(la + sa);
    const totalTax = roundTo2(lt + st);
    const totalCommission = roundTo2(totalGross + totalTax);
    const shortfall = roundTo2(Math.max(0, totalCommission - dep));

    const nv = (row, key) => roundTo2(parseFloat(row?.[key] ?? 0) || 0);
    const feeCreditOpts = {
      description: commissionDescription,
      date: commissionDate,
    };

    const txs = [];

    txs.push({
      accountNumber: "50100",
      accountName: "Agent's Commission",
      debit: la,
      credit: 0,
      description: commissionDescription,
      date: commissionDate,
    });
    txs.push({
      accountNumber: "21500",
      accountName: "Commission Payable",
      debit: 0,
      credit: nv(l0, "netCommission"),
      description: commissionDescription,
      date: commissionDate,
    });
    txs.push({
      accountNumber: "21500",
      accountName: "Commission Payable",
      debit: 0,
      credit: nv(s0, "netCommission"),
      description: commissionDescription,
      date: commissionDate,
    });
    txs.push({
      accountNumber: "23001",
      accountName: "HST Input Tax Credit",
      debit: lt,
      credit: 0,
      description: commissionDescription,
      date: commissionDate,
    });
    txs.push({
      accountNumber: "44100",
      accountName: "Fee Deducted Income",
      debit: 0,
      credit: nv(l0, "feesDeducted"),
      description: commissionDescription,
      date: commissionDate,
    });
    txs.push(...buildAgentFeeTaxCreditLines(l0, feeCreditOpts));
    txs.push({
      accountNumber: "44100",
      accountName: "Fee Deducted Income",
      debit: 0,
      credit: nv(s0, "feesDeducted"),
      description: commissionDescription,
      date: commissionDate,
    });
    txs.push(...buildAgentFeeTaxCreditLines(s0, feeCreditOpts));

    for (let i = 1; i < n; i++) {
      const li = groups[i]?.listingRow || {};
      const si = groups[i]?.sellingRow || {};
      txs.push({
        accountNumber: "44100",
        accountName: "Fee Deducted Income",
        debit: 0,
        credit: nv(li, "feesDeducted"),
        description: commissionDescription,
        date: commissionDate,
      });
      txs.push(...buildAgentFeeTaxCreditLines(li, feeCreditOpts));
      txs.push({
        accountNumber: "21500",
        accountName: "Commission Payable",
        debit: 0,
        credit: nv(li, "netCommission"),
        description: commissionDescription,
        date: commissionDate,
      });
      txs.push({
        accountNumber: "21500",
        accountName: "Commission Payable",
        debit: 0,
        credit: nv(si, "netCommission"),
        description: commissionDescription,
        date: commissionDate,
      });
    }

    txs.push({
      accountNumber: "50100",
      accountName: "Agent's Commission",
      debit: dep,
      credit: 0,
      description: commissionDescription,
      date: commissionDate,
    });
    txs.push({
      accountNumber: "23001",
      accountName: "HST Input Tax Credit",
      debit: st,
      credit: 0,
      description: commissionDescription,
      date: commissionDate,
    });
    txs.push({
      accountNumber: "40100",
      accountName: "Commission Income",
      debit: 0,
      credit: totalGross,
      description: commissionDescription,
      date: commissionDate,
    });
    txs.push({
      accountNumber: "23000",
      accountName: "HST Collected",
      debit: 0,
      credit: totalTax,
      description: commissionDescription,
      date: commissionDate,
    });
    // Accrue A/R for amount still owed when deposit < total commission (finalized date).
    // Confirm Payment then posts 10004 Dr / 12200 Cr on closing date to record cash and clear A/R.
    if (shortfall > 0) {
      txs.push({
        accountNumber: "12200",
        accountName: "A/R - Commission From Deals",
        debit: shortfall,
        credit: 0,
        description: commissionDescription,
        date: commissionDate,
      });
    }
    txs.push({
      accountNumber: "21300",
      accountName: "Deposit Liability",
      debit: sa,
      credit: 0,
      description: commissionDescription,
      date: commissionDate,
    });

    for (let i = 1; i < n; i++) {
      const si = groups[i]?.sellingRow || {};
      txs.push({
        accountNumber: "44100",
        accountName: "Fee Deducted Income",
        debit: 0,
        credit: nv(si, "feesDeducted"),
        description: commissionDescription,
        date: commissionDate,
      });
      txs.push(...buildAgentFeeTaxCreditLines(si, feeCreditOpts));
    }

    return txs;
  };

  const absorbPennyDifferenceOn21300 = (txList) => {
    const totalDebit = txList.reduce(
      (sum, t) => sum + (parseFloat(t.debit) || 0),
      0,
    );
    const totalCredit = txList.reduce(
      (sum, t) => sum + (parseFloat(t.credit) || 0),
      0,
    );
    const diff = roundTo2(totalDebit - totalCredit);
    if (Math.abs(diff) <= 0 || Math.abs(diff) >= 0.02) return;
    const bal = txList.find((t) => t.accountNumber === "21300");
    if (!bal) return;
    if (diff > 0) {
      bal.credit = roundTo2((parseFloat(bal.credit) || 0) + diff);
    } else {
      bal.debit = roundTo2((parseFloat(bal.debit) || 0) + Math.abs(diff));
    }
  };

  // Helper to calculate tax and total

  const getCommissionAndTaxTotal = () => {
    let commission = 0;

    if (end === "Selling Side") {
      commission = parseFloat(commissionData.selling?.sellingAmount || 0);
    } else if (end === "Listing Side") {
      commission = parseFloat(commissionData.listing?.listingAmount || 0);
    }

    const tax = commission * 0.13;

    const total = commission + tax;

    return {
      commission: commission ? commission.toFixed(2) : "-",

      tax: commission ? tax.toFixed(2) : "-",

      total: commission ? total.toFixed(2) : "-",
    };
  };

  const { commission, tax, total } = getCommissionAndTaxTotal();

  // Helper to calculate total commission (Listing Amount + Selling Amount + Listing Tax + Selling Tax)

  const getTotalCommission = () => {
    const listingAmount = parseFloat(
      commissionData.listing?.listingAmount || 0,
    );

    const sellingAmount = parseFloat(
      commissionData.selling?.sellingAmount || 0,
    );

    const listingTax = parseFloat(commissionData.listing?.listingTax || 0);

    const sellingTax = parseFloat(commissionData.selling?.sellingTax || 0);

    const totalCommission =
      listingAmount + sellingAmount + listingTax + sellingTax;

    return totalCommission > 0 ? totalCommission.toFixed(2) : "-";
  };

  // Helper to calculate total buyer rebate from all agents

  const getTotalBuyerRebate = () => {
    const totalBuyerRebate = (trade.agentCommissionList || []).reduce(
      (total, agent) => {
        if (agent.buyerRebateIncluded === "yes" && agent.buyerRebateAmount) {
          return total + parseFloat(agent.buyerRebateAmount || 0);
        }

        return total;
      },

      0,
    );

    return totalBuyerRebate > 0 ? totalBuyerRebate.toFixed(2) : "-";
  };

  // Helper to get the trade address

  const getTradeAddress = () => {
    const k = trade?.keyInfo || {};

    return `${k.streetNumber || ""} ${k.streetName || ""}`.trim();
  };

  // Outside broker company that pays commission (listing or cooperating side by trade type).
  const getOutsideBrokerCompanyNameForPayment = () => {
    const classification = String(
      trade?.keyInfo?.classification || "",
    ).toUpperCase();
    const wantCoop = classification === "CO-OPERATING SIDE";

    const brokerMatchesSide = (broker) => {
      const type = String(broker?.type || "").toLowerCase();
      const end = String(broker?.end || "").toLowerCase();
      if (wantCoop) {
        return (
          type.includes("cooperat") ||
          type.includes("selling") ||
          end.includes("selling")
        );
      }
      return type.includes("listing") || end.includes("listing");
    };

    if (trade?.outsideBrokers?.length > 0) {
      const broker =
        trade.outsideBrokers.find(brokerMatchesSide) ||
        trade.outsideBrokers.find((b) => String(b?.company || "").trim());
      if (broker?.company) return String(broker.company).trim();
    }

    const commissionRows =
      trade?.commission?.outsideBrokersRows || trade?.outsideBrokersRows || [];
    for (const row of commissionRows) {
      const end = String(row?.end || row?.type || "").toLowerCase();
      const name = String(row?.brokerage || row?.company || "").trim();
      if (!name) continue;
      if (wantCoop && end.includes("selling")) return name;
      if (!wantCoop && end.includes("listing")) return name;
    }
    for (const row of commissionRows) {
      const name = String(row?.brokerage || row?.company || "").trim();
      if (name) return name;
    }

    return "";
  };

  /** Listing lawyer (Seller Lawyer) for Confirm Payment "Received From". */
  const getListingLawyerReceivedFrom = () => {
    const people = trade?.people || [];
    const sellerLawyer = people.find(
      (p) =>
        String(p?.type || "")
          .trim()
          .toLowerCase() === "seller lawyer",
    );
    if (!sellerLawyer) return "";

    const company = String(
      sellerLawyer.companyName || sellerLawyer.company || "",
    ).trim();
    if (company) return company;

    const name =
      `${sellerLawyer.firstName || ""} ${sellerLawyer.lastName || ""}`.trim() ||
      String(sellerLawyer.name || "").trim();
    return name;
  };

  /** Best default for Confirm Payment "Received From" — listing lawyer, then outside broker. */
  const getDefaultReceivedFrom = () => {
    const listingLawyer = getListingLawyerReceivedFrom();
    if (listingLawyer) return listingLawyer;
    const brokerCompany = getOutsideBrokerCompanyNameForPayment();
    if (brokerCompany) return brokerCompany;
    return "";
  };

  // Helper to get selling amount + tax from commission income section

  const getSellingAmountAndTax = () => {
    const sellingAmount = parseFloat(
      commissionData.selling?.sellingAmount || 0,
    );

    const sellingTax = parseFloat(commissionData.selling?.sellingTax || 0);

    return (sellingAmount + sellingTax).toFixed(2);
  };

  // Auto-populate payment popup fields when Confirm Payment opens

  useEffect(() => {
    if (!showPaymentPopup) return;

    const weHoldValue = getWeHoldValue();
    const depositAmount = parseFloat(getDepositAmount()) || 0;
    const totalCommission = parseFloat(getTotalCommission()) || 0;

    const defaultReceivedFrom = getDefaultReceivedFrom();
    if (defaultReceivedFrom) setReceivedFrom(defaultReceivedFrom);

    if (!paymentAmount.trim()) {
      if (weHoldValue === "No") {
        const sellingAmountAndTax = getSellingAmountAndTax();
        if (sellingAmountAndTax !== "0.00") {
          setPaymentAmount(sellingAmountAndTax);
        } else if (totalCommission > 0) {
          setPaymentAmount(totalCommission.toFixed(2));
        }
      } else if (totalCommission > depositAmount) {
        // We Hold = Yes (or unknown) with shortfall — amount due is commission less deposit
        const remainingAmount = (totalCommission - depositAmount).toFixed(2);
        if (remainingAmount !== "0.00") {
          setPaymentAmount(remainingAmount);
        }
      } else if (totalCommission > 0) {
        setPaymentAmount(totalCommission.toFixed(2));
      }
    }
  }, [showPaymentPopup, trade, paymentAmount]);

  // Update getTransactionDetails to remove the 21500 Commission Payable debit transaction

  const getTransactionDetails = () => {
    const address = getTradeAddress();

    const commissionDescription = `Trade #: ${trade.tradeNumber} - ${address}`;

    const commissionDate = finalizedDate;

    const closingDateForTransactions = closingDate || todayStr;

    console.log("=== getTransactionDetails - Date Assignment ===");

    console.log("Finalized Date (Blue section):", commissionDate);

    console.log("Closing Date (Red section):", closingDateForTransactions);

    const depositAmount = parseFloat(getDepositAmount()) || 0;

    // Handle dual agent vs single agent scenarios

    let agentNet, agentCommission, agentHST, feesDeducted, taxOnFeesDeducted;
    let agentFeeCreditAgents = [];

    if (shouldShowDualAgentFields()) {
      // For dual agents, sum up the values from both agents

      const agent1Data = getAgent1Data();

      const agent2Data = getAgent2Data();
      agentFeeCreditAgents = [agent1Data, agent2Data];

      agentNet =
        parseFloat(agent1Data.netCommission || 0) +
        parseFloat(agent2Data.netCommission || 0);

      agentCommission =
        parseFloat(agent1Data.amount || 0) + parseFloat(agent2Data.amount || 0);

      agentHST =
        parseFloat(agent1Data.tax || 0) + parseFloat(agent2Data.tax || 0);

      feesDeducted =
        parseFloat(agent1Data.feesDeducted || 0) +
        parseFloat(agent2Data.feesDeducted || 0);

      // Calculate tax on fees deducted for both agents

      const agent1TaxOnFees = agent1Data.feesDeducted
        ? parseFloat(agent1Data.feesDeducted) * 0.13
        : 0;

      const agent2TaxOnFees = agent2Data.feesDeducted
        ? parseFloat(agent2Data.feesDeducted) * 0.13
        : 0;

      taxOnFeesDeducted = agent1TaxOnFees + agent2TaxOnFees;
    } else {
      // Single agent scenario
      agentFeeCreditAgents = [agentData];

      agentNet = parseFloat(agentData.netCommission || 0);

      agentCommission = parseFloat(agentData.amount || 0);

      agentHST = parseFloat(agentData.tax || 0);

      feesDeducted = parseFloat(agentData.feesDeducted || 0);

      taxOnFeesDeducted =
        agentData.totalFees && agentData.feesDeducted
          ? parseFloat(agentData.totalFees) - parseFloat(agentData.feesDeducted)
          : 0;
    }

    const listingAmount = parseFloat(
      commissionData.listing?.listingAmount || 0,
    );

    const listingTax = parseFloat(commissionData.listing?.listingTax || 0);

    const sellingAmount = parseFloat(
      commissionData.selling?.sellingAmount || 0,
    );

    const sellingTax = parseFloat(commissionData.selling?.sellingTax || 0);

    const weHold = getWeHoldValue();

    const popupAmount = parseFloat(paymentAmount) || 0;

    const popupDescription = `Trade #: ${trade.tradeNumber} - Received From: ${receivedFrom}`;

    const popupDate = finalizedDate || todayStr;

    if (weHold === "Yes") {
      console.log("=== We Hold = Yes - Creating Transaction Preview ===");

      console.log(
        "Blue section (transactions 1-9) will use date:",

        commissionDate,
      );

      console.log(
        "Red section (transactions 10-12) will use date:",

        closingDateForTransactions,
      );

      let transactions = [];

      if (shouldUseAgentDoubleEnderMultiAgentPostings()) {
        transactions = buildAgentDoubleEnderMultiAgentLedgerRows({
          commissionDescription,
          commissionDate,
          listingAmount,
          sellingAmount,
          listingTax,
          sellingTax,
          depositAmount,
        });
        absorbPennyDifferenceOn21300(transactions);
      } else if (shouldShowDualAgentFields()) {
        // Dual agent transactions for We Hold = Yes
        const agent1Data = getAgent1Data();
        const agent2Data = getAgent2Data();

        transactions = [
          // 1. Listing Tax - 23001 - D
          {
            accountNumber: "23001",
            accountName: "HST Input Tax Credit",
            debit: listingTax,
            credit: 0,
            description: commissionDescription,
            date: commissionDate,
          },
          // 2. Deposit - Total Commission - 21300 - C (credit)
          {
            accountNumber: "21300",
            accountName: "Deposit Liability",
            debit:
              depositAmount >=
              listingAmount + sellingAmount + listingTax + sellingTax
                ? 0
                : roundTo2(
                    Math.abs(
                      depositAmount -
                        (listingAmount +
                          sellingAmount +
                          listingTax +
                          sellingTax),
                    ),
                  ),
            credit:
              depositAmount >=
              listingAmount + sellingAmount + listingTax + sellingTax
                ? roundTo2(
                    Math.abs(
                      depositAmount -
                        (listingAmount +
                          sellingAmount +
                          listingTax +
                          sellingTax),
                    ),
                  )
                : 0,
            description: commissionDescription,
            date: commissionDate,
          },
          // 5. Deposit - 21300 - D
          {
            accountNumber: "21300",
            accountName: "Deposit Liability",
            debit: depositAmount,
            credit: 0,
            description: commissionDescription,
            date: commissionDate,
          },
          // 6. Listing Tax + Selling Tax - 23000 - C
          {
            accountNumber: "23000",
            accountName: "HST Collected",
            debit: 0,
            credit: roundTo2(listingTax + sellingTax),
            description: commissionDescription,
            date: commissionDate,
          },
          // 7. Listing + Selling Amount - 40100 - C
          {
            accountNumber: "40100",
            accountName: "Commission Income",
            debit: 0,
            credit: roundTo2(listingAmount + sellingAmount),
            description: commissionDescription,
            date: commissionDate,
          },
          // 8. Agent 1: Plan HST → 23001; OE → 12100; commission deduction → 12101
          ...buildAgentFeeTaxCreditLines(agent1Data, {
            description: commissionDescription,
            date: commissionDate,
          }),
          // 9. Agent 1 Fees Deducted - 44100 - C
          {
            accountNumber: "44100",
            accountName: "Fee Deducted Income",
            debit: 0,
            credit: roundTo2(parseFloat(agent1Data.feesDeducted || 0)),
            description: commissionDescription,
            date: commissionDate,
          },
          // 10. Selling tax - 23001 - D
          {
            accountNumber: "23001",
            accountName: "HST Input Tax Credit",
            debit: sellingTax,
            credit: 0,
            description: commissionDescription,
            date: commissionDate,
          },
          // 11. Listing amount - 50100 - D (listing side transaction)
          {
            accountNumber: "50100",
            accountName: "Agent's Commission",
            debit: listingAmount,
            credit: 0,
            description: commissionDescription,
            date: commissionDate,
          },
          // 11b. Selling-side payout:
          // - If there is an outside broker, this is not an Agent's Commission row.
          // - Instead it becomes Outside Broker Payable/Commission + HST Input (matches sheet).
          ...(trade?.outsideBrokers && trade.outsideBrokers.length > 0
            ? [
                {
                  accountNumber: "21100",
                  accountName: "Outside Broker Payable",
                  debit: 0,
                  credit: roundTo2(sellingAmount + sellingTax),
                  description: commissionDescription,
                  date: commissionDate,
                },
                {
                  accountNumber: "51100",
                  accountName: "Outside Broker Commission",
                  debit: sellingAmount,
                  credit: 0,
                  description: commissionDescription,
                  date: commissionDate,
                },
              ]
            : [
                {
                  accountNumber: "50100",
                  accountName: "Agent's Commission",
                  debit: sellingAmount,
                  credit: 0,
                  description: commissionDescription,
                  date: commissionDate,
                },
              ]),
          // 12. Agent 1 net commission - 21500 - C
          {
            accountNumber: "21500",
            accountName: "Commission Payable",
            debit: 0,
            credit: roundTo2(parseFloat(agent1Data.netCommission || 0)),
            description: commissionDescription,
            date: commissionDate,
          },
          // 13. Agent 2 Net Commission - 21500 - C
          {
            accountNumber: "21500",
            accountName: "Commission Payable",
            debit: 0,
            credit: roundTo2(parseFloat(agent2Data.netCommission || 0)),
            description: commissionDescription,
            date: commissionDate,
          },
          // 14. Agent 2 Fees Deducted - 44100 - C
          {
            accountNumber: "44100",
            accountName: "Fee Deducted Income",
            debit: 0,
            credit: roundTo2(parseFloat(agent2Data.feesDeducted || 0)),
            description: commissionDescription,
            date: commissionDate,
          },
          // 15. Agent 2: Plan HST → 23001; OE → 12100; commission deduction → 12101
          ...buildAgentFeeTaxCreditLines(agent2Data, {
            description: commissionDescription,
            date: commissionDate,
          }),
        ];
      } else {
        // Single agent transactions (existing logic)
        transactions = [
          // Blue marked section (first 9 transactions) - use finalized date
          {
            accountNumber: "21500",
            accountName: "Commission Payable",
            debit: 0,
            credit: agentNet,
            description: commissionDescription,
            date: commissionDate, // finalized date
          },
          {
            accountNumber: "50100",
            accountName: "Agent's Commission",
            debit: listingAmount,
            credit: 0,
            description: commissionDescription,
            date: commissionDate, // finalized date
          },
          // Selling-side payout: Outside broker vs in-house agent
          ...(trade?.outsideBrokers && trade.outsideBrokers.length > 0
            ? [
                {
                  accountNumber: "21100",
                  accountName: "Outside Broker Payable",
                  debit: 0,
                  credit: roundTo2(sellingAmount + sellingTax),
                  description: commissionDescription,
                  date: commissionDate, // finalized date
                },
                {
                  accountNumber: "51100",
                  accountName: "Outside Broker Commission",
                  debit: sellingAmount,
                  credit: 0,
                  description: commissionDescription,
                  date: commissionDate, // finalized date
                },
                {
                  accountNumber: "23001",
                  accountName: "HST Input Tax Credit",
                  debit: sellingTax,
                  credit: 0,
                  description: commissionDescription,
                  date: commissionDate, // finalized date
                },
              ]
            : [
                {
                  accountNumber: "50100",
                  accountName: "Agent's Commission",
                  debit: sellingAmount,
                  credit: 0,
                  description: commissionDescription,
                  date: commissionDate, // finalized date
                },
              ]),
          {
            accountNumber: "23001",
            accountName: "HST Input Tax Credit",
            debit: agentHST,
            credit: 0,
            description: commissionDescription,
            date: commissionDate, // finalized date
          },
          {
            accountNumber: "44100",
            accountName: "Fee Deducted Income",
            debit: 0,
            credit: feesDeducted,
            description: commissionDescription,
            date: commissionDate, // finalized date
          },
          ...agentFeeCreditAgents.flatMap((a) =>
            buildAgentFeeTaxCreditLines(a, {
              description: commissionDescription,
              date: commissionDate,
            }),
          ),
          {
            accountNumber: "40100",
            accountName: "Commission Income",
            debit: 0,
            credit: listingAmount + sellingAmount,
            description: commissionDescription,
            date: commissionDate, // finalized date
          },
          {
            accountNumber: "23000",
            accountName: "HST Collected",
            debit: 0,
            credit: listingTax + sellingTax,
            description: commissionDescription,
            date: commissionDate, // finalized date
          },
          {
            accountNumber: "21300",
            accountName: "Deposit Liability",
            debit: depositAmount,
            credit: 0,
            description: commissionDescription,
            date: commissionDate, // finalized date
          },
          // Deposit - Total Commission posted to 21300 as credit (or debit when negative)
          {
            accountNumber: "21300",
            accountName: "Deposit Liability",
            debit:
              depositAmount >=
              listingAmount + sellingAmount + listingTax + sellingTax
                ? 0
                : listingAmount +
                  sellingAmount +
                  listingTax +
                  sellingTax -
                  depositAmount,
            credit:
              depositAmount >=
              listingAmount + sellingAmount + listingTax + sellingTax
                ? depositAmount -
                  (listingAmount + sellingAmount + listingTax + sellingTax)
                : 0,
            description: commissionDescription,
            date: commissionDate, // finalized date
          },
          // Selling-side payout:
          // - If there is an outside broker, post Outside Broker Payable + Commission + HST Input (star-marked rows).
          // - Otherwise keep the simplified agent commission selling postings.
          ...(trade?.outsideBrokers && trade.outsideBrokers.length > 0
            ? [
                {
                  accountNumber: "21100",
                  accountName: "Outside Broker Payable",
                  debit: 0,
                  credit: sellingAmount + sellingTax,
                  description: commissionDescription,
                  date: commissionDate, // finalized date
                },
                {
                  accountNumber: "51100",
                  accountName: "Outside Broker Commission",
                  debit: sellingAmount,
                  credit: 0,
                  description: commissionDescription,
                  date: commissionDate, // finalized date
                },
                {
                  accountNumber: "23001",
                  accountName: "HST Input Tax Credit",
                  debit: sellingTax,
                  credit: 0,
                  description: commissionDescription,
                  date: commissionDate, // finalized date
                },
              ]
            : [
                {
                  accountNumber: "50100",
                  accountName: "Agent's Commission",
                  debit: sellingAmount,
                  credit: 0,
                  description: commissionDescription,
                  date: commissionDate, // finalized date
                },
                {
                  accountNumber: "23001",
                  accountName: "HST Input Tax Credit",
                  debit: sellingTax,
                  credit: 0,
                  description: commissionDescription,
                  date: commissionDate, // finalized date
                },
              ]),
        ];
      }

      // Add final 2 transactions when Total Commission > Deposit Amount
      // Single agent: show shortfall pair in preview without popup.
      // Dual agent / Agent Double Ender (2+ agents): show when payment popup has amount (matches Confirm Payment).

      const totalCommission = parseFloat(getTotalCommission()) || 0;

      if (totalCommission > depositAmount) {
        const needsPaymentPopupForPreview =
          shouldShowDualAgentFields() ||
          shouldUseAgentDoubleEnderMultiAgentPostings();
        const shouldShowFinalTransactions =
          !needsPaymentPopupForPreview || popupAmount > 0;

        if (shouldShowFinalTransactions) {
          const shortfallAmount = roundTo2(totalCommission - depositAmount);
          const finalPairAmount =
            popupAmount > 0 ? roundTo2(popupAmount) : shortfallAmount;

          const finalDescription = `Trade #: ${trade.tradeNumber} - ${address} - Received From: ${receivedFrom}`;

          transactions.push({
            accountNumber: "10004",

            accountName: "Cash - Commission Trust Account",

            debit: finalPairAmount,

            credit: 0,

            description: finalDescription,

            date: closingDateForTransactions, // use closing date for final 2 transactions
          });

          transactions.push({
            accountNumber: "12200",

            accountName: "A/R - Commission From Deals",

            debit: 0,

            credit: finalPairAmount,

            description: finalDescription,

            date: closingDateForTransactions, // use closing date for final 2 transactions
          });
        }
      }

      // Balance rounding: absorb penny differences in 21300 (Deposit - Total Commission)
      const totalDebitPrev = transactions.reduce(
        (sum, t) => sum + (parseFloat(t.debit) || 0),
        0,
      );
      const totalCreditPrev = transactions.reduce(
        (sum, t) => sum + (parseFloat(t.credit) || 0),
        0,
      );
      const diffPrev = roundTo2(totalDebitPrev - totalCreditPrev);
      if (Math.abs(diffPrev) > 0 && Math.abs(diffPrev) < 0.02) {
        // Find 21300 "Deposit - Total Commission" (has credit or debit based on deposit vs total)
        let balPrev;
        if (shouldUseAgentDoubleEnderMultiAgentPostings()) {
          balPrev = transactions.find((t) => t.accountNumber === "21300");
        } else {
          const balIdxPrev = showDualSideLayout() ? 0 : 9;
          balPrev = transactions[balIdxPrev];
        }
        if (balPrev && balPrev.accountNumber === "21300") {
          if (diffPrev > 0) {
            balPrev.credit = roundTo2(
              (parseFloat(balPrev.credit) || 0) + diffPrev,
            );
          } else {
            balPrev.debit = roundTo2(
              (parseFloat(balPrev.debit) || 0) + Math.abs(diffPrev),
            );
          }
        }
      }

      return transactions;
    } else {
      // New logic for We Hold = No.
      // Assumption: when We Hold = "No" there is always at least one referral agent,
      // and all referral postings plus each agent's postings should be created here.

      const num = (v) => parseFloat(v || 0);

      const listingAmount = num(commissionData.listing?.listingAmount || 0);
      const sellingAmount = num(commissionData.selling?.sellingAmount || 0);
      const listingTax = num(commissionData.listing?.listingTax || 0);
      const sellingTax = num(commissionData.selling?.sellingTax || 0);

      const commissionTotal = listingAmount + sellingAmount;
      const taxTotal = listingTax + sellingTax;
      const grandTotal = commissionTotal + taxTotal;

      const allAgents = trade.agentCommissionList || [];
      const referralAgents = allAgents.filter(
        (a) => (a.classification || "").toUpperCase() === "REFERRAL",
      );
      const nonReferralAgents = allAgents.filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );

      let totalReferralNet = 0;
      let totalAgentNet = 0;

      const transactions = [];

      // Header: income, tax, and AR total
      transactions.push(
        {
          accountNumber: "40100",
          accountName: "Commission Income",
          debit: 0,
          credit: commissionTotal,
          description: commissionDescription,
          date: commissionDate,
        },
        {
          accountNumber: "23000",
          accountName: "HST Collected",
          debit: 0,
          credit: taxTotal,
          description: commissionDescription,
          date: commissionDate,
        },
        {
          accountNumber: "12200",
          accountName: "A/R - Commission From Deals",
          debit: grandTotal,
          credit: 0,
          description: commissionDescription,
          date: commissionDate,
        },
      );

      // Referral block(s)
      referralAgents.forEach((agent) => {
        const referralCommission = num(agent.amount);
        const referralNet = num(agent.netCommission);
        const referralDiff = referralNet - referralCommission;
        totalReferralNet += referralNet;

        transactions.push(
          {
            accountNumber: "21100",
            accountName: "Outside Broker Payable",
            debit: 0,
            credit: referralNet,
            description: commissionDescription,
            date: commissionDate,
          },
          {
            accountNumber: "51100",
            accountName: "Commission To Other Brokers",
            debit: referralCommission,
            credit: 0,
            description: commissionDescription,
            date: commissionDate,
          },
          {
            accountNumber: "23001",
            accountName: "HST Input Tax Credit",
            debit: referralDiff,
            credit: 0,
            description: commissionDescription,
            date: commissionDate,
          },
        );
      });

      // Per-agent block(s) for non-referral agents
      nonReferralAgents.forEach((agent) => {
        const commissionAmt = num(agent.amount);
        const feesDeducted = num(agent.feesDeducted);
        const totalFees = num(agent.totalFees);
        const taxOnFees = totalFees - feesDeducted;
        const net = num(agent.netCommission);
        totalAgentNet += net;

        // Extra debit on 23001 as described:
        const extra23001Debit = feesDeducted + taxOnFees + net - commissionAmt;

        transactions.push(
          {
            accountNumber: "50100",
            accountName: "Agent's Commission",
            debit: commissionAmt,
            credit: 0,
            description: commissionDescription,
            date: commissionDate,
          },
          {
            accountNumber: "23001",
            accountName: "HST Input Tax Credit",
            debit: extra23001Debit,
            credit: 0,
            description: commissionDescription,
            date: commissionDate,
          },
        );

        if (feesDeducted !== 0) {
          transactions.push({
            accountNumber: "44100",
            accountName: "Fee Deducted Income",
            debit: 0,
            credit: feesDeducted,
            description: commissionDescription,
            date: commissionDate,
          });
        }

        // Plan HST → 23001; office expenses → 12100; commission deduction → 12101
        transactions.push(
          ...buildAgentFeeTaxCreditLines(agent, {
            description: commissionDescription,
            date: commissionDate,
          }),
        );

        // 11. 21500-c-Agent Net only (no 21500-d, no 10004-c per agent)
        if (net !== 0) {
          transactions.push({
            accountNumber: "21500",
            accountName: "Commission Payable",
            debit: 0,
            credit: net,
            description: commissionDescription,
            date: commissionDate,
          });
        }
      });

      // Transactions 12 & 13 (10004-c-total, 12200-d-total) are NOT posted here.
      // They are posted only in the second stage when user confirms payment,
      // with description including "Received From: [name]".

      return transactions;
    }
  };

  // Update handleConfirmPayment to remove the 21500 Commission Payable debit transaction

  const handleConfirmPayment = async () => {
    // Check if trade is already finalized

    if (trade.isFinalized) {
      toast.error("This trade is already finalized.");

      return;
    }

    // Validate required fields

    if (!finalizedDate) {
      toast.error("Please enter the finalized date.");

      return;
    }

    // Validate closing date when We Hold = Yes

    const weHoldValue = getWeHoldValue();

    if (weHoldValue === "Yes" && !closingDate) {
      toast.error(
        "Please ensure the closing date is set when 'We Hold = Yes'.",
      );

      return;
    }

    if (!receivedFrom.trim()) {
      toast.error("Please enter who the payment was received from.");

      return;
    }

    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error("Please enter a valid payment amount.");

      return;
    }

    // Validate EFT number when deposit amount is less than total commission

    const depositAmount = parseFloat(getDepositAmount()) || 0;

    const totalCommission = parseFloat(getTotalCommission()) || 0;

    if (totalCommission > depositAmount && !eftNumber) {
      toast.error(
        "EFT number is required when deposit amount is less than total commission.",
      );

      return;
    }

    setIsFinalizing(true);

    try {
      const address = getTradeAddress();

      const commissionDescription = `Trade #: ${trade.tradeNumber} - ${address}`;

      const commissionDate = finalizedDate;

      const closingDateForTransactions = closingDate || todayStr;

      const depositAmount = parseFloat(getDepositAmount()) || 0;

      // Handle dual agent vs single agent scenarios

      let agentNet, agentCommission, agentHST, feesDeducted, taxOnFeesDeducted;
      let agentFeeCreditAgents = [];

      if (shouldShowDualAgentFields()) {
        // For dual agents, sum up the values from both agents

        const agent1Data = getAgent1Data();

        const agent2Data = getAgent2Data();
        agentFeeCreditAgents = [agent1Data, agent2Data];

        agentNet =
          parseFloat(agent1Data.netCommission || 0) +
          parseFloat(agent2Data.netCommission || 0);

        agentCommission =
          parseFloat(agent1Data.awardAmount || 0) +
          parseFloat(agent2Data.awardAmount || 0);

        agentHST =
          parseFloat(agent1Data.tax || 0) + parseFloat(agent2Data.tax || 0);

        feesDeducted =
          parseFloat(agent1Data.feesDeducted || 0) +
          parseFloat(agent2Data.feesDeducted || 0);

        // Calculate tax on fees deducted for both agents

        const agent1TaxOnFees = agent1Data.feesDeducted
          ? parseFloat(agent1Data.feesDeducted) * 0.13
          : 0;

        const agent2TaxOnFees = agent2Data.feesDeducted
          ? parseFloat(agent2Data.feesDeducted) * 0.13
          : 0;

        taxOnFeesDeducted = agent1TaxOnFees + agent2TaxOnFees;
      } else {
        // Single agent scenario
        agentFeeCreditAgents = [agentData];

        agentNet = parseFloat(agentData.netCommission || 0);

        agentCommission = parseFloat(agentData.amount || 0);

        agentHST = parseFloat(agentData.tax || 0);

        feesDeducted = parseFloat(agentData.feesDeducted || 0);

        taxOnFeesDeducted =
          agentData.totalFees && agentData.feesDeducted
            ? parseFloat(agentData.totalFees) -
              parseFloat(agentData.feesDeducted)
            : 0;
      }

      const listingAmount = parseFloat(
        commissionData.listing?.listingAmount || 0,
      );

      const listingTax = parseFloat(commissionData.listing?.listingTax || 0);

      const sellingAmount = parseFloat(
        commissionData.selling?.sellingAmount || 0,
      );

      const sellingTax = parseFloat(commissionData.selling?.sellingTax || 0);

      const weHold = getWeHoldValue();

      if (weHold === "Yes") {
        console.log("=== WE HOLD = YES - Starting ledger transactions ===");

        console.log("Commission Description:", commissionDescription);

        console.log("Commission Date (Blue section):", commissionDate);

        console.log("Closing Date (Red section):", closingDateForTransactions);

        console.log("Trade Number:", trade.tradeNumber);

        console.log("Address:", address);

        // Updated logic for We Hold = Yes

        try {
          if (shouldUseAgentDoubleEnderMultiAgentPostings()) {
            console.log(
              "=== POSTING AGENT DOUBLE ENDER (2+ AGENTS) TRANSACTIONS ===",
            );
            const adeTransactions = buildAgentDoubleEnderMultiAgentLedgerRows({
              commissionDescription,
              commissionDate,
              listingAmount,
              sellingAmount,
              listingTax,
              sellingTax,
              depositAmount,
            });
            absorbPennyDifferenceOn21300(adeTransactions);
            for (let i = 0; i < adeTransactions.length; i++) {
              const transaction = adeTransactions[i];
              console.log(
                `Posting ADE transaction ${i + 1}: ${transaction.accountNumber} - ${transaction.accountName}`,
              );
              await axiosInstance.post("/ledger", transaction);
            }
            console.log(
              "=== ALL AGENT DOUBLE ENDER MULTI-AGENT TRANSACTIONS POSTED ===",
            );
            const popupAmountAde = parseFloat(paymentAmount) || 0;
            const totalCommAde = parseFloat(getTotalCommission()) || 0;
            if (totalCommAde > depositAmount && popupAmountAde > 0) {
              const popupDescriptionAde = `Trade #: ${trade.tradeNumber} - ${address} - Received From: ${receivedFrom}`;
              await axiosInstance.post("/ledger", {
                accountNumber: "10004",
                accountName: "Cash - Commission Trust Account",
                debit: popupAmountAde,
                credit: 0,
                description: popupDescriptionAde,
                date: closingDateForTransactions,
                eftNumber: eftNumber,
                reference: `EFT#${eftNumber}`,
              });
              await axiosInstance.post("/ledger", {
                accountNumber: "12200",
                accountName: "A/R - Commission From Deals",
                debit: 0,
                credit: popupAmountAde,
                description: popupDescriptionAde,
                date: closingDateForTransactions,
                eftNumber: eftNumber,
                reference: `EFT#${eftNumber}`,
              });
              console.log(
                "=== ADE SHORTFALL: 10004 Dr / 12200 Cr POSTED SUCCESSFULLY ===",
              );
            }
          } else if (shouldShowDualAgentFields()) {
            // Dual agent transactions for We Hold = Yes (legacy dual-row pattern)
            console.log("=== POSTING DUAL AGENT TRANSACTIONS ===");
            const agent1Data = getAgent1Data();
            const agent2Data = getAgent2Data();

            const dualAgentTransactions = [
              // 1. Listing Tax - 23001 - D
              {
                accountNumber: "23001",
                accountName: "HST Input Tax Credit",
                debit: listingTax,
                credit: 0,
                description: commissionDescription,
                date: commissionDate,
              },
              // 2. Deposit - Total Commission - 21300 - C (credit) or D (debit when negative)
              {
                accountNumber: "21300",
                accountName: "Deposit Liability",
                debit:
                  depositAmount >=
                  listingAmount + sellingAmount + listingTax + sellingTax
                    ? 0
                    : roundTo2(
                        Math.abs(
                          depositAmount -
                            (listingAmount +
                              sellingAmount +
                              listingTax +
                              sellingTax),
                        ),
                      ),
                credit:
                  depositAmount >=
                  listingAmount + sellingAmount + listingTax + sellingTax
                    ? roundTo2(
                        Math.abs(
                          depositAmount -
                            (listingAmount +
                              sellingAmount +
                              listingTax +
                              sellingTax),
                        ),
                      )
                    : 0,
                description: commissionDescription,
                date: commissionDate,
              },
              // 5. Deposit - 21300 - D
              {
                accountNumber: "21300",
                accountName: "Deposit Liability",
                debit: depositAmount,
                credit: 0,
                description: commissionDescription,
                date: commissionDate,
              },
              // 6. Listing Tax + Selling Tax - 23000 - C
              {
                accountNumber: "23000",
                accountName: "HST Collected",
                debit: 0,
                credit: roundTo2(listingTax + sellingTax),
                description: commissionDescription,
                date: commissionDate,
              },
              // 7. Listing + Selling Amount - 40100 - C
              {
                accountNumber: "40100",
                accountName: "Commission Income",
                debit: 0,
                credit: roundTo2(listingAmount + sellingAmount),
                description: commissionDescription,
                date: commissionDate,
              },
              // 8. Agent 1: Plan HST → 23001; OE → 12100; commission deduction → 12101
              ...buildAgentFeeTaxCreditLines(agent1Data, {
                description: commissionDescription,
                date: commissionDate,
              }),
              // 9. Agent 1 Fees Deducted - 44100 - C
              {
                accountNumber: "44100",
                accountName: "Fee Deducted Income",
                debit: 0,
                credit: roundTo2(parseFloat(agent1Data.feesDeducted || 0)),
                description: commissionDescription,
                date: commissionDate,
              },
              // 10. Selling tax - 23001 - D
              {
                accountNumber: "23001",
                accountName: "HST Input Tax Credit",
                debit: sellingTax,
                credit: 0,
                description: commissionDescription,
                date: commissionDate,
              },
              // 11. Listing amount - 50100 - D (listing side transaction)
              {
                accountNumber: "50100",
                accountName: "Agent's Commission",
                debit: listingAmount,
                credit: 0,
                description: commissionDescription,
                date: commissionDate,
              },
              // 11b. Selling amount - 50100 - D (selling side transaction)
              {
                accountNumber: "50100",
                accountName: "Agent's Commission",
                debit: sellingAmount,
                credit: 0,
                description: commissionDescription,
                date: commissionDate,
              },
              // 12. Agent 1 net commission - 21500 - C
              {
                accountNumber: "21500",
                accountName: "Commission Payable",
                debit: 0,
                credit: roundTo2(parseFloat(agent1Data.netCommission || 0)),
                description: commissionDescription,
                date: commissionDate,
              },
              // 13. Agent 2 Net Commission - 21500 - C
              {
                accountNumber: "21500",
                accountName: "Commission Payable",
                debit: 0,
                credit: roundTo2(parseFloat(agent2Data.netCommission || 0)),
                description: commissionDescription,
                date: commissionDate,
              },
              // 14. Agent 2 Fees Deducted - 44100 - C
              {
                accountNumber: "44100",
                accountName: "Fee Deducted Income",
                debit: 0,
                credit: roundTo2(parseFloat(agent2Data.feesDeducted || 0)),
                description: commissionDescription,
                date: commissionDate,
              },
              // 15. Agent 2: Plan HST → 23001; OE → 12100; commission deduction → 12101
              ...buildAgentFeeTaxCreditLines(agent2Data, {
                description: commissionDescription,
                date: commissionDate,
              }),
            ];

            // Balance rounding: absorb penny differences in 21300 (Deposit - Total Commission)
            const totalDebit = dualAgentTransactions.reduce(
              (sum, t) => sum + (parseFloat(t.debit) || 0),
              0,
            );
            const totalCredit = dualAgentTransactions.reduce(
              (sum, t) => sum + (parseFloat(t.credit) || 0),
              0,
            );
            const diff = roundTo2(totalDebit - totalCredit);
            if (Math.abs(diff) > 0 && Math.abs(diff) < 0.02) {
              const balIdx = 1; // 21300 Deposit - Total Commission
              const bal = dualAgentTransactions[balIdx];
              if (bal && bal.accountNumber === "21300") {
                if (diff > 0) {
                  bal.credit = roundTo2((parseFloat(bal.credit) || 0) + diff);
                } else {
                  bal.debit = roundTo2(
                    (parseFloat(bal.debit) || 0) + Math.abs(diff),
                  );
                }
              }
            }

            // Post all dual agent transactions
            for (let i = 0; i < dualAgentTransactions.length; i++) {
              const transaction = dualAgentTransactions[i];
              console.log(
                `Posting transaction ${i + 1}: ${transaction.accountNumber} - ${
                  transaction.accountName
                } with date: ${transaction.date}`,
              );
              await axiosInstance.post("/ledger", transaction);
              console.log(`Transaction ${i + 1} posted successfully`);
            }

            console.log(
              "=== ALL DUAL AGENT TRANSACTIONS POSTED SUCCESSFULLY ===",
            );

            // Post payment popup transactions for dual agents
            const popupAmount = parseFloat(paymentAmount) || 0;
            const popupDescription = `Trade #: ${trade.tradeNumber} - ${address} - Received From: ${receivedFrom}`;

            // 16. Cash - Commission Trust Account - 10004 - d (debit)
            console.log(
              "Posting payment transaction 1: 10004 - Cash - Commission Trust Account (debit) with date:",
              closingDateForTransactions,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "10004",
              accountName: "Cash - Commission Trust Account",
              debit: popupAmount,
              credit: 0,
              description: popupDescription,
              date: closingDateForTransactions,
              eftNumber: eftNumber,
              reference: `EFT#${eftNumber}`,
            });

            // 17. A/R - Commission From Deals - 12200 - c (credit)
            console.log(
              "Posting payment transaction 2: 12200 - A/R - Commission From Deals (credit) with date:",
              closingDateForTransactions,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "12200",
              accountName: "A/R - Commission From Deals",
              debit: 0,
              credit: popupAmount,
              description: popupDescription,
              date: closingDateForTransactions,
              eftNumber: eftNumber,
              reference: `EFT#${eftNumber}`,
            });

            console.log(
              "=== PAYMENT POPUP TRANSACTIONS POSTED SUCCESSFULLY ===",
            );
          } else {
            // Single agent transactions (existing logic)
            console.log("=== POSTING SINGLE AGENT TRANSACTIONS ===");

            // Blue marked section (first 9 transactions) - use finalized date
            console.log("=== POSTING BLUE SECTION (Transactions 1-9) ===");
            console.log(
              "Using finalized date for all blue section transactions:",
              commissionDate,
            );

            // 1. Commission Payable - 21500 - c (credit)
            console.log(
              "Posting transaction 1: 21500 - Commission Payable (credit) with date:",
              commissionDate,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "21500",
              accountName: "Commission Payable",
              debit: 0,
              credit: agentNet,
              description: commissionDescription,
              date: commissionDate,
            });

            // 2. Agent's Commission - 50100 - d (debit)
            console.log(
              "Posting transaction 2: 50100 - Agent's Commission (debit) with date:",
              commissionDate,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "50100",
              accountName: "Agent's Commission",
              debit: listingAmount,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            });

            // 3. HST Input Tax Credit - 23001 - d (debit)
            console.log(
              "Posting transaction 3: 23001 - HST Input Tax Credit (debit) with date:",
              commissionDate,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "23001",
              accountName: "HST Input Tax Credit",
              debit: listingTax,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            });

            // 4. Fee Deducted Income - 44100 - c (credit)
            console.log(
              "Posting transaction 4: 44100 - Fee Deducted Income (credit) with date:",
              commissionDate,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "44100",
              accountName: "Fee Deducted Income",
              debit: 0,
              credit: feesDeducted,
              description: commissionDescription,
              date: commissionDate,
            });

            // 5. Plan HST → 23001; office expenses → 12100; commission deduction → 12101
            console.log(
              "Posting fee / office-expense tax credits with date:",
              commissionDate,
            );
            for (const a of agentFeeCreditAgents) {
              await postAgentFeeTaxCreditLines(a, {
                description: commissionDescription,
                date: commissionDate,
              });
            }

            // 6. Commission Income - 40100 - c (credit)
            console.log(
              "Posting transaction 6: 40100 - Commission Income (credit) with date:",
              commissionDate,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "40100",
              accountName: "Commission Income",
              debit: 0,
              credit: listingAmount + sellingAmount,
              description: commissionDescription,
              date: commissionDate,
            });

            // 7. HST Collected - 23000 - c (credit)
            console.log(
              "Posting transaction 7: 23000 - HST Collected (credit) with date:",
              commissionDate,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "23000",
              accountName: "HST Collected",
              debit: 0,
              credit: listingTax + sellingTax,
              description: commissionDescription,
              date: commissionDate,
            });

            // 8. Deposit Liability - 21300 - d (debit)
            console.log(
              "Posting transaction 8: 21300 - Deposit Liability (debit) with date:",
              commissionDate,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "21300",
              accountName: "Deposit Liability",
              debit: depositAmount,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            });

            // 9. Deposit - Total Commission - 21300 as credit (or debit when negative)
            const totalCommissionAmount =
              listingAmount + sellingAmount + listingTax + sellingTax;
            if (depositAmount >= totalCommissionAmount) {
              console.log(
                "Posting transaction 9: 21300 - Deposit Liability (credit) with date:",
                commissionDate,
              );
              await axiosInstance.post("/ledger", {
                accountNumber: "21300",
                accountName: "Deposit Liability",
                debit: 0,
                credit: depositAmount - totalCommissionAmount,
                description: commissionDescription,
                date: commissionDate,
              });
            } else {
              console.log(
                "Posting transaction 9: 21300 - Deposit Liability (debit) with date:",
                commissionDate,
              );
              await axiosInstance.post("/ledger", {
                accountNumber: "21300",
                accountName: "Deposit Liability",
                debit: totalCommissionAmount - depositAmount,
                credit: 0,
                description: commissionDescription,
                date: commissionDate,
              });
            }

            // Post final 2 transactions when deposit < total commission (regardless of popup)
            if (depositAmount < totalCommissionAmount) {
              const shortfallAmount = totalCommissionAmount - depositAmount;
              const finalDescription = `Trade #: ${trade.tradeNumber} - ${address} - Received From: ${receivedFrom}`;
              console.log(
                "Posting final transaction 1: 10004 - Cash - Commission Trust Account (debit) with date:",
                closingDateForTransactions,
              );
              await axiosInstance.post("/ledger", {
                accountNumber: "10004",
                accountName: "Cash - Commission Trust Account",
                debit: shortfallAmount,
                credit: 0,
                description: finalDescription,
                date: closingDateForTransactions,
                eftNumber: eftNumber,
                reference: `EFT#${eftNumber}`,
              });

              console.log(
                "Posting final transaction 2: 12200 - A/R - Commission From Deals (credit) with date:",
                closingDateForTransactions,
              );
              await axiosInstance.post("/ledger", {
                accountNumber: "12200",
                accountName: "A/R - Commission From Deals",
                debit: 0,
                credit: shortfallAmount,
                description: finalDescription,
                date: closingDateForTransactions,
                eftNumber: eftNumber,
                reference: `EFT#${eftNumber}`,
              });
            }

            // Red marked section (last transactions) - use finalized date
            console.log("=== POSTING RED SECTION (Transactions 10-11) ===");
            console.log(
              "Using finalized date for all red section transactions:",
              commissionDate,
            );

            if (trade?.outsideBrokers && trade.outsideBrokers.length > 0) {
              // Outside broker selling-side postings (these must hit Trial Balance)
              await axiosInstance.post("/ledger", {
                accountNumber: "21100",
                accountName: "Outside Broker Payable",
                debit: 0,
                credit: roundTo2(sellingAmount + sellingTax),
                description: commissionDescription,
                date: commissionDate,
              });
              await axiosInstance.post("/ledger", {
                accountNumber: "51100",
                accountName: "Outside Broker Commission",
                debit: sellingAmount,
                credit: 0,
                description: commissionDescription,
                date: commissionDate,
              });
              await axiosInstance.post("/ledger", {
                accountNumber: "23001",
                accountName: "HST Input Tax Credit",
                debit: sellingTax,
                credit: 0,
                description: commissionDescription,
                date: commissionDate,
              });
            } else {
              // In-house selling-side postings
              await axiosInstance.post("/ledger", {
                accountNumber: "50100",
                accountName: "Agent's Commission",
                debit: sellingAmount,
                credit: 0,
                description: commissionDescription,
                date: commissionDate,
              });
              await axiosInstance.post("/ledger", {
                accountNumber: "23001",
                accountName: "HST Input Tax Credit",
                debit: sellingTax,
                credit: 0,
                description: commissionDescription,
                date: commissionDate,
              });
            }

            console.log(
              "=== ALL SINGLE AGENT TRANSACTIONS POSTED SUCCESSFULLY ===",
            );
          }
        } catch (error) {
          console.error("Error posting We Hold = Yes transactions:", error);

          throw error; // Re-throw to be caught by the outer catch block
        }
      } else {
        // We Hold = No: post transactions 1-11 only (from getTransactionDetails),
        // then in this second stage post the two payment lines with "Received From: [name]".

        const weHoldNoTransactions = getTransactionDetails();
        for (let i = 0; i < weHoldNoTransactions.length; i++) {
          const t = weHoldNoTransactions[i];
          await axiosInstance.post("/ledger", {
            accountNumber: t.accountNumber,
            accountName: t.accountName,
            debit: t.debit || 0,
            credit: t.credit || 0,
            description: t.description,
            date: t.date,
          });
        }

        const popupAmount = parseFloat(paymentAmount) || 0;
        const paymentDescription = `Trade #: ${trade.tradeNumber} - ${getTradeAddress()} - Received From: ${receivedFrom}`;

        // Second stage only: last 2 transactions (10004 D, 12200 C) with Received From in description
        if (popupAmount > 0) {
          await axiosInstance.post("/ledger", {
            accountNumber: "10004",
            accountName: "Cash - Commission Trust Account",
            debit: popupAmount,
            credit: 0,
            description: paymentDescription,
            date: closingDateForTransactions,
            eftNumber: eftNumber,
            reference: `EFT#${eftNumber}`,
          });
          await axiosInstance.post("/ledger", {
            accountNumber: "12200",
            accountName: "A/R - Commission From Deals",
            debit: 0,
            credit: popupAmount,
            description: paymentDescription,
            date: closingDateForTransactions,
            eftNumber: eftNumber,
            reference: `EFT#${eftNumber}`,
          });
        }

        const totalBuyerRebate = parseFloat(getTotalBuyerRebate()) || 0;
        if (totalBuyerRebate > 0) {
          await axiosInstance.post("/ledger", {
            accountNumber: "52100",
            accountName: "Referral Fees",
            debit: 0,
            credit: totalBuyerRebate,
            description: `Trade #: ${trade.tradeNumber} - Buyer Rebate`,
            date: commissionDate,
          });
        }
      }

      // Additional transactions when Total Commission > Deposit Amount

      // Note: These transactions are already posted above in the main transaction block

      // No need to post them again here to avoid duplicates

      // The main transaction block already handles:

      // - 10004 - Cash - Commission Trust Account (debit) at line 738

      // - 12200 - A/R - Commission From Deals (credit) at line 752

      // Buyer rebate transaction is already posted above in the main transaction block

      // No need to post it again here to avoid duplicates

      // Finalize the trade after processing all transactions

      const response = await axiosInstance.post(
        `/trades/finalize/${trade.tradeNumber}`,

        { finalizedDate: finalizedDate },
      );

      if (response.data && response.data.success) {
        toast.success("Trade finalized and posted to ledger successfully!");

        setShowPaymentPopup(false);

        setEftNumber(""); // Reset EFT number after successful payment

        if (onClose) onClose();

        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event("refreshTrialBalance"));
        }
      } else {
        toast.error(response.data.message || "Failed to finalize trade.");
      }
    } catch (err) {
      toast.error("Error finalizing trade. Please try again.");
    } finally {
      setIsFinalizing(false);
    }
  };

  // This function now checks hold field and shows payment popup if needed

  const handleFinalizeTrade = async () => {
    if (trade.isFinalized) {
      toast.error("This trade is already finalized.");

      return;
    }

    // Validate finalized date

    if (!finalizedDate) {
      toast.error("Please enter the finalized date.");

      return;
    }

    // Validate closing date when We Hold = Yes

    const weHoldValue = getWeHoldValue();

    if (weHoldValue === "Yes" && !closingDate) {
      toast.error(
        "Please ensure the closing date is set when 'We Hold = Yes'.",
      );

      return;
    }

    if (fallenThru) {
      // Mark trade as finalized but do not process any transactions or update Trial Balance

      try {
        const response = await axiosInstance.post(
          `/trades/finalize/${trade.tradeNumber}`,

          { fallenThru: true, finalizedDate: finalizedDate },
        );

        if (response.data && response.data.success) {
          toast.success(
            "Trade marked as Fallen Thru. No transactions processed.",
          );

          setEftNumber(""); // Reset EFT number when marking as fallen thru

          if (onClose) onClose();
        } else {
          toast.error(
            response.data.message || "Failed to mark trade as Fallen Thru.",
          );
        }
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          toast.error(error.response.data.message);
        } else {
          toast.error("An error occurred while marking trade as Fallen Thru.");
        }
      }

      return;
    }

    // Check if hold field is "NO" or if Total Commission is higher than Deposit - show payment popup

    const depositAmount = parseFloat(getDepositAmount()) || 0;

    const totalCommission = parseFloat(getTotalCommission()) || 0;

    if (weHoldValue === "No" || totalCommission > depositAmount) {
      // Generate new EFT number each time the popup is opened

      generateNextEFTNumber()
        .then((nextNum) => {
          console.log("Generated EFT number:", nextNum);
          setEftNumber(
            nextNum != null && nextNum !== ""
              ? String(nextNum)
              : String(generateLocalEFTNumber() ?? ""),
          );
          setShowPaymentPopup(true);
        })
        .catch((error) => {
          console.error("Error generating EFT number:", error);
          const localEFTNumber = generateLocalEFTNumber();
          console.log("Using local EFT number:", localEFTNumber);
          setEftNumber(
            localEFTNumber != null && localEFTNumber !== ""
              ? String(localEFTNumber)
              : "",
          );
          setShowPaymentPopup(true);
        });

      return;
    }

    // For "Yes" hold field, post all required transactions

    try {
      // --- BEGIN WE HOLD = YES LOGIC ---

      const address = getTradeAddress();

      const commissionDescription = `Trade #: ${trade.tradeNumber} - ${address}`;

      const commissionDate = finalizedDate;

      const closingDateForTransactions = closingDate || todayStr;

      const depositAmount = parseFloat(getDepositAmount()) || 0;

      // Handle dual agent vs single agent scenarios

      let agentNet, agentCommission, agentHST, feesDeducted, taxOnFeesDeducted;
      let agentFeeCreditAgents = [];

      if (shouldShowDualAgentFields()) {
        // For dual agents, sum up the values from both agents

        const agent1Data = getAgent1Data();

        const agent2Data = getAgent2Data();
        agentFeeCreditAgents = [agent1Data, agent2Data];

        agentNet =
          parseFloat(agent1Data.netCommission || 0) +
          parseFloat(agent2Data.netCommission || 0);

        agentCommission =
          parseFloat(agent1Data.awardAmount || 0) +
          parseFloat(agent2Data.awardAmount || 0);

        agentHST =
          parseFloat(agent1Data.tax || 0) + parseFloat(agent2Data.tax || 0);

        feesDeducted =
          parseFloat(agent1Data.feesDeducted || 0) +
          parseFloat(agent2Data.feesDeducted || 0);

        // Calculate tax on fees deducted for both agents

        const agent1TaxOnFees = agent1Data.feesDeducted
          ? parseFloat(agent1Data.feesDeducted) * 0.13
          : 0;

        const agent2TaxOnFees = agent2Data.feesDeducted
          ? parseFloat(agent2Data.feesDeducted) * 0.13
          : 0;

        taxOnFeesDeducted = agent1TaxOnFees + agent2TaxOnFees;
      } else {
        // Single agent scenario
        agentFeeCreditAgents = [agentData];

        agentNet = parseFloat(agentData.netCommission || 0);

        agentCommission = parseFloat(agentData.amount || 0);

        agentHST = parseFloat(agentData.tax || 0);

        feesDeducted = parseFloat(agentData.feesDeducted || 0);

        taxOnFeesDeducted =
          agentData.totalFees && agentData.feesDeducted
            ? parseFloat(agentData.totalFees) -
              parseFloat(agentData.feesDeducted)
            : 0;
      }

      const listingAmount = parseFloat(
        commissionData.listing?.listingAmount || 0,
      );

      const listingTax = parseFloat(commissionData.listing?.listingTax || 0);

      const sellingAmount = parseFloat(
        commissionData.selling?.sellingAmount || 0,
      );

      const sellingTax = parseFloat(commissionData.selling?.sellingTax || 0);

      // Post all transactions for We Hold = Yes

      console.log("=== WE HOLD = YES - Starting ledger transactions ===");

      console.log("Commission Description:", commissionDescription);

      console.log("Commission Date (Blue section):", commissionDate);

      console.log("Closing Date (Red section):", closingDateForTransactions);

      console.log("Trade Number:", trade.tradeNumber);

      console.log("Address:", address);

      try {
        if (shouldUseAgentDoubleEnderMultiAgentPostings()) {
          console.log(
            "=== POSTING AGENT DOUBLE ENDER (2+ AGENTS) TRANSACTIONS ===",
          );
          const adeTransactions2 = buildAgentDoubleEnderMultiAgentLedgerRows({
            commissionDescription,
            commissionDate,
            listingAmount,
            sellingAmount,
            listingTax,
            sellingTax,
            depositAmount,
          });
          absorbPennyDifferenceOn21300(adeTransactions2);
          for (let i = 0; i < adeTransactions2.length; i++) {
            const transaction = adeTransactions2[i];
            await axiosInstance.post("/ledger", transaction);
          }
          console.log(
            "=== ALL AGENT DOUBLE ENDER MULTI-AGENT TRANSACTIONS POSTED ===",
          );
          const popupAmountAde2 = parseFloat(paymentAmount) || 0;
          const totalCommAde2 = parseFloat(getTotalCommission()) || 0;
          if (totalCommAde2 > depositAmount && popupAmountAde2 > 0) {
            const popupDescriptionAde2 = `Trade #: ${trade.tradeNumber} - ${address} - Received From: ${receivedFrom}`;
            await axiosInstance.post("/ledger", {
              accountNumber: "10004",
              accountName: "Cash - Commission Trust Account",
              debit: popupAmountAde2,
              credit: 0,
              description: popupDescriptionAde2,
              date: closingDateForTransactions,
              eftNumber: eftNumber,
              reference: `EFT#${eftNumber}`,
            });
            await axiosInstance.post("/ledger", {
              accountNumber: "12200",
              accountName: "A/R - Commission From Deals",
              debit: 0,
              credit: popupAmountAde2,
              description: popupDescriptionAde2,
              date: closingDateForTransactions,
              eftNumber: eftNumber,
              reference: `EFT#${eftNumber}`,
            });
            console.log(
              "=== ADE SHORTFALL: 10004 Dr / 12200 Cr POSTED SUCCESSFULLY ===",
            );
          }
        } else if (shouldShowDualAgentFields()) {
          // Dual agent transactions for We Hold = Yes (legacy dual-row pattern)
          console.log("=== POSTING DUAL AGENT TRANSACTIONS ===");
          const agent1Data = getAgent1Data();
          const agent2Data = getAgent2Data();

          const dualAgentTransactions = [
            // 1. Listing Tax - 23001 - D
            {
              accountNumber: "23001",
              accountName: "HST Input Tax Credit",
              debit: listingTax,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            },
            // 2. Deposit - Total Commission - 21300 - C (credit) or D (debit when negative)
            {
              accountNumber: "21300",
              accountName: "Deposit Liability",
              debit:
                depositAmount >=
                listingAmount + sellingAmount + listingTax + sellingTax
                  ? 0
                  : roundTo2(
                      Math.abs(
                        depositAmount -
                          (listingAmount +
                            sellingAmount +
                            listingTax +
                            sellingTax),
                      ),
                    ),
              credit:
                depositAmount >=
                listingAmount + sellingAmount + listingTax + sellingTax
                  ? roundTo2(
                      Math.abs(
                        depositAmount -
                          (listingAmount +
                            sellingAmount +
                            listingTax +
                            sellingTax),
                      ),
                    )
                  : 0,
              description: commissionDescription,
              date: commissionDate,
            },
            // 5. Deposit - 21300 - D
            {
              accountNumber: "21300",
              accountName: "Deposit Liability",
              debit: depositAmount,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            },
            // 6. Listing Tax + Selling Tax - 23000 - C
            {
              accountNumber: "23000",
              accountName: "HST Collected",
              debit: 0,
              credit: roundTo2(listingTax + sellingTax),
              description: commissionDescription,
              date: commissionDate,
            },
            // 7. Listing + Selling Amount - 40100 - C
            {
              accountNumber: "40100",
              accountName: "Commission Income",
              debit: 0,
              credit: roundTo2(listingAmount + sellingAmount),
              description: commissionDescription,
              date: commissionDate,
            },
            // 8. Agent 1: Plan HST → 23001; OE → 12100; commission deduction → 12101
            ...buildAgentFeeTaxCreditLines(agent1Data, {
              description: commissionDescription,
              date: commissionDate,
            }),
            // 9. Agent 1 Fees Deducted - 44100 - C
            {
              accountNumber: "44100",
              accountName: "Fee Deducted Income",
              debit: 0,
              credit: roundTo2(parseFloat(agent1Data.feesDeducted || 0)),
              description: commissionDescription,
              date: commissionDate,
            },
            // 10. Selling tax - 23001 - D
            {
              accountNumber: "23001",
              accountName: "HST Input Tax Credit",
              debit: sellingTax,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            },
            // 11. Listing amount - 50100 - D (listing side transaction)
            {
              accountNumber: "50100",
              accountName: "Agent's Commission",
              debit: listingAmount,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            },
            // 11b. Selling amount - 50100 - D (selling side transaction)
            {
              accountNumber: "50100",
              accountName: "Agent's Commission",
              debit: sellingAmount,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            },
            // 12. Agent 1 net commission - 21500 - C
            {
              accountNumber: "21500",
              accountName: "Commission Payable",
              debit: 0,
              credit: roundTo2(parseFloat(agent1Data.netCommission || 0)),
              description: commissionDescription,
              date: commissionDate,
            },
            // 13. Agent 2 Net Commission - 21500 - C
            {
              accountNumber: "21500",
              accountName: "Commission Payable",
              debit: 0,
              credit: roundTo2(parseFloat(agent2Data.netCommission || 0)),
              description: commissionDescription,
              date: commissionDate,
            },
            // 14. Agent 2 Fees Deducted - 44100 - C
            {
              accountNumber: "44100",
              accountName: "Fee Deducted Income",
              debit: 0,
              credit: roundTo2(parseFloat(agent2Data.feesDeducted || 0)),
              description: commissionDescription,
              date: commissionDate,
            },
            // 15. Agent 2: Plan HST → 23001; OE → 12100; commission deduction → 12101
            ...buildAgentFeeTaxCreditLines(agent2Data, {
              description: commissionDescription,
              date: commissionDate,
            }),
          ];

          // Balance rounding: absorb penny differences in 21300 (Deposit - Total Commission)
          const totalDebit2 = dualAgentTransactions.reduce(
            (sum, t) => sum + (parseFloat(t.debit) || 0),
            0,
          );
          const totalCredit2 = dualAgentTransactions.reduce(
            (sum, t) => sum + (parseFloat(t.credit) || 0),
            0,
          );
          const diff2 = roundTo2(totalDebit2 - totalCredit2);
          if (Math.abs(diff2) > 0 && Math.abs(diff2) < 0.02) {
            const balIdx2 = 1;
            const bal2 = dualAgentTransactions[balIdx2];
            if (bal2 && bal2.accountNumber === "21300") {
              if (diff2 > 0) {
                bal2.credit = roundTo2((parseFloat(bal2.credit) || 0) + diff2);
              } else {
                bal2.debit = roundTo2(
                  (parseFloat(bal2.debit) || 0) + Math.abs(diff2),
                );
              }
            }
          }

          // Post all dual agent transactions
          for (let i = 0; i < dualAgentTransactions.length; i++) {
            const transaction = dualAgentTransactions[i];
            console.log(
              `Posting transaction ${i + 1}: ${transaction.accountNumber} - ${
                transaction.accountName
              } with date: ${transaction.date}`,
            );
            await axiosInstance.post("/ledger", transaction);
            console.log(`Transaction ${i + 1} posted successfully`);
          }

          console.log(
            "=== ALL DUAL AGENT TRANSACTIONS POSTED SUCCESSFULLY ===",
          );

          // Post payment popup transactions for dual agents
          const popupAmount = parseFloat(paymentAmount) || 0;
          const popupDescription = `Trade #: ${trade.tradeNumber} - ${address} - Received From: ${receivedFrom}`;

          // 16. Cash - Commission Trust Account - 10004 - d (debit)
          console.log(
            "Posting payment transaction 1: 10004 - Cash - Commission Trust Account (debit) with date:",
            closingDateForTransactions,
          );
          await axiosInstance.post("/ledger", {
            accountNumber: "10004",
            accountName: "Cash - Commission Trust Account",
            debit: popupAmount,
            credit: 0,
            description: popupDescription,
            date: closingDateForTransactions,
            eftNumber: eftNumber,
            reference: `EFT#${eftNumber}`,
          });

          // 17. A/R - Commission From Deals - 12200 - c (credit)
          console.log(
            "Posting payment transaction 2: 12200 - A/R - Commission From Deals (credit) with date:",
            closingDateForTransactions,
          );
          await axiosInstance.post("/ledger", {
            accountNumber: "12200",
            accountName: "A/R - Commission From Deals",
            debit: 0,
            credit: popupAmount,
            description: popupDescription,
            date: closingDateForTransactions,
            eftNumber: eftNumber,
            reference: `EFT#${eftNumber}`,
          });

          console.log("=== PAYMENT POPUP TRANSACTIONS POSTED SUCCESSFULLY ===");
        } else {
          // Single agent transactions (existing logic)
          console.log("=== POSTING SINGLE AGENT TRANSACTIONS ===");

          // Blue marked section (first 9 transactions) - use finalized date
          console.log("=== POSTING BLUE SECTION (Transactions 1-9) ===");
          console.log(
            "Using finalized date for all blue section transactions:",
            commissionDate,
          );

          // 1. Commission Payable - 21500 - c (credit)
          console.log(
            "Posting transaction 1: 21500 - Commission Payable (credit) with date:",
            commissionDate,
          );
          await axiosInstance.post("/ledger", {
            accountNumber: "21500",
            accountName: "Commission Payable",
            debit: 0,
            credit: agentNet,
            description: commissionDescription,
            date: commissionDate,
          });

          // 2. Agent's Commission - 50100 - d (debit)
          console.log(
            "Posting transaction 2: 50100 - Agent's Commission (debit) with date:",
            commissionDate,
          );
          await axiosInstance.post("/ledger", {
            accountNumber: "50100",
            accountName: "Agent's Commission",
            debit: listingAmount,
            credit: 0,
            description: commissionDescription,
            date: commissionDate,
          });

          // 3. HST Input Tax Credit - 23001 - d (debit)
          console.log(
            "Posting transaction 3: 23001 - HST Input Tax Credit (debit) with date:",
            commissionDate,
          );
          await axiosInstance.post("/ledger", {
            accountNumber: "23001",
            accountName: "HST Input Tax Credit",
            debit: listingTax,
            credit: 0,
            description: commissionDescription,
            date: commissionDate,
          });

          // 4. Fee Deducted Income - 44100 - c (credit)
          console.log(
            "Posting transaction 4: 44100 - Fee Deducted Income (credit) with date:",
            commissionDate,
          );
          await axiosInstance.post("/ledger", {
            accountNumber: "44100",
            accountName: "Fee Deducted Income",
            debit: 0,
            credit: feesDeducted,
            description: commissionDescription,
            date: commissionDate,
          });

          // 5. Plan HST → 23001; office expenses → 12100; commission deduction → 12101
          console.log(
            "Posting fee / office-expense tax credits with date:",
            commissionDate,
          );
          for (const a of agentFeeCreditAgents) {
            await postAgentFeeTaxCreditLines(a, {
              description: commissionDescription,
              date: commissionDate,
            });
          }

          // 6. Commission Income - 40100 - c (credit)
          console.log(
            "Posting transaction 6: 40100 - Commission Income (credit) with date:",
            commissionDate,
          );
          await axiosInstance.post("/ledger", {
            accountNumber: "40100",
            accountName: "Commission Income",
            debit: 0,
            credit: listingAmount + sellingAmount,
            description: commissionDescription,
            date: commissionDate,
          });

          // 7. HST Collected - 23000 - c (credit)
          console.log(
            "Posting transaction 7: 23000 - HST Collected (credit) with date:",
            commissionDate,
          );
          await axiosInstance.post("/ledger", {
            accountNumber: "23000",
            accountName: "HST Collected",
            debit: 0,
            credit: listingTax + sellingTax,
            description: commissionDescription,
            date: commissionDate,
          });

          // 8. Deposit Liability - 21300 - d (debit)
          console.log(
            "Posting transaction 8: 21300 - Deposit Liability (debit) with date:",
            commissionDate,
          );
          await axiosInstance.post("/ledger", {
            accountNumber: "21300",
            accountName: "Deposit Liability",
            debit: depositAmount,
            credit: 0,
            description: commissionDescription,
            date: commissionDate,
          });

          // 9. Deposit - Total Commission - 21300 as credit (or debit when negative)
          const totalCommissionAmount =
            listingAmount + sellingAmount + listingTax + sellingTax;
          if (depositAmount >= totalCommissionAmount) {
            console.log(
              "Posting transaction 9: 21300 - Deposit Liability (credit) with date:",
              commissionDate,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "21300",
              accountName: "Deposit Liability",
              debit: 0,
              credit: depositAmount - totalCommissionAmount,
              description: commissionDescription,
              date: commissionDate,
            });
          } else {
            console.log(
              "Posting transaction 9: 21300 - Deposit Liability (debit) with date:",
              commissionDate,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "21300",
              accountName: "Deposit Liability",
              debit: totalCommissionAmount - depositAmount,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            });
          }

          // Post final 2 transactions when deposit < total commission (regardless of popup)
          if (depositAmount < totalCommissionAmount) {
            const shortfallAmount = totalCommissionAmount - depositAmount;
            const finalDescription = `Trade #: ${trade.tradeNumber} - ${address} - Received From: ${receivedFrom}`;
            console.log(
              "Posting final transaction 1: 10004 - Cash - Commission Trust Account (debit) with date:",
              closingDateForTransactions,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "10004",
              accountName: "Cash - Commission Trust Account",
              debit: shortfallAmount,
              credit: 0,
              description: finalDescription,
              date: closingDateForTransactions,
              eftNumber: eftNumber,
              reference: `EFT#${eftNumber}`,
            });

            console.log(
              "Posting final transaction 2: 12200 - A/R - Commission From Deals (credit) with date:",
              closingDateForTransactions,
            );
            await axiosInstance.post("/ledger", {
              accountNumber: "12200",
              accountName: "A/R - Commission From Deals",
              debit: 0,
              credit: shortfallAmount,
              description: finalDescription,
              date: closingDateForTransactions,
              eftNumber: eftNumber,
              reference: `EFT#${eftNumber}`,
            });
          }

          // Red marked section (last transactions) - use finalized date
          console.log("=== POSTING RED SECTION (Transactions 10-11) ===");
          console.log(
            "Using finalized date for all red section transactions:",
            commissionDate,
          );

          if (trade?.outsideBrokers && trade.outsideBrokers.length > 0) {
            await axiosInstance.post("/ledger", {
              accountNumber: "21100",
              accountName: "Outside Broker Payable",
              debit: 0,
              credit: roundTo2(sellingAmount + sellingTax),
              description: commissionDescription,
              date: commissionDate,
            });
            await axiosInstance.post("/ledger", {
              accountNumber: "51100",
              accountName: "Outside Broker Commission",
              debit: sellingAmount,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            });
            await axiosInstance.post("/ledger", {
              accountNumber: "23001",
              accountName: "HST Input Tax Credit",
              debit: sellingTax,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            });
          } else {
            await axiosInstance.post("/ledger", {
              accountNumber: "50100",
              accountName: "Agent's Commission",
              debit: sellingAmount,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            });
            await axiosInstance.post("/ledger", {
              accountNumber: "23001",
              accountName: "HST Input Tax Credit",
              debit: sellingTax,
              credit: 0,
              description: commissionDescription,
              date: commissionDate,
            });
          }

          console.log(
            "=== ALL SINGLE AGENT TRANSACTIONS POSTED SUCCESSFULLY ===",
          );
        }
      } catch (error) {
        console.error("Error posting We Hold = Yes transactions:", error);

        throw error; // Re-throw to be caught by the outer catch block
      }

      // Finalize the trade after processing all transactions

      const response = await axiosInstance.post(
        `/trades/finalize/${trade.tradeNumber}`,

        { finalizedDate: finalizedDate },
      );

      if (response.data && response.data.success) {
        toast.success("Trade finalized and posted to ledger successfully!");

        setEftNumber(""); // Reset EFT number after successful finalization

        if (onClose) onClose();

        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event("refreshTrialBalance"));
        }
      } else {
        toast.error(response.data.message || "Failed to finalize trade.");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error("An error occurred while finalizing the trade.");
      }
    }
  };

  // Helper to get ledger entries for a given EFT number

  const getLedgerEntriesForEFT = (eftNumber) => {
    return ledgerEntries.filter(
      (entry) => String(entry.eftNumber) === String(eftNumber),
    );
  };

  // Function to generate next EFT number

  const generateNextEFTNumber = async () => {
    try {
      const response = await axiosInstance.get("/eft/next-number");

      if (response.data && response.data.eftNumber) {
        return response.data.eftNumber;
      }

      // Fallback to local generation if backend fails

      return generateLocalEFTNumber();
    } catch (error) {
      console.error("Error getting next EFT number from backend:", error);

      // Fallback to local generation if backend fails

      return generateLocalEFTNumber();
    }
  };

  // Fallback local EFT number generation (matches backend /api/eft range: 4000+)
  // Parses both "EFT123" and numeric "123" from ledger entries

  const generateLocalEFTNumber = () => {
    const existingEFTNumbers = ledgerEntries
      .filter(
        (entry) =>
          entry.eftNumber != null && String(entry.eftNumber).trim() !== "",
      )
      .map((entry) => {
        const raw = String(entry.eftNumber).trim();
        const match = raw.match(/EFT(\d+)/i);
        if (match) return parseInt(match[1], 10);
        const num = parseInt(raw, 10);
        return Number.isNaN(num) ? 0 : num;
      })
      .filter((n) => n > 0);

    const nextNumber =
      existingEFTNumbers.length > 0
        ? Math.max(...existingEFTNumbers) + 1
        : 4000;

    return nextNumber;
  };

  // Print handler

  const handlePrintDetails = () => {
    let details;
    let finalizeReversalRows = [];
    let commissionReversalRows = [];
    let postFinalizeActivityRows = [];
    let suppressFinalizeReversalTable = false;

    if (trade.isFinalized) {
      const tradeLedgerRows = ledgerEntries.filter(
        (row) => row?.description && rowBelongsToTrade(row, trade),
      );

      finalizeReversalRows = tradeLedgerRows.filter((row) =>
        isFinalizeBatchReversalLedgerRow(row, trade.tradeNumber),
      );

      commissionReversalRows = tradeLedgerRows.filter((row) =>
        isCommissionEftReversalLedgerRow(row, trade.tradeNumber),
      );

      postFinalizeActivityRows = tradeLedgerRows.filter((row) =>
        isPostFinalizeActivityLedgerRow(row, trade.tradeNumber),
      );

      // Original finalize journal (exclude reversal mirrors & post-finalize modules)
      const isMainTransactionDetailRow = (row) =>
        !isReversalLedgerRow(row, trade.tradeNumber) &&
        !isPostFinalizeActivityLedgerRow(row, trade.tradeNumber) &&
        !isCommissionTrustOffJournalLedgerRow(row) &&
        !row.description.includes("Paid to:") &&
        // Trust deposit (Real Estate Trust) rows are displayed in their own table below
        !(
          (row.accountNumber === "10002" || row.accountNumber === "21300") &&
          /received\s+from/i.test(String(row.description || ""))
        ) &&
        !(
          row.description.includes("Commission Trust Payment") &&
          !row.description.includes("Received From:") &&
          !row.description.includes("Trade #:")
        );

      details = tradeLedgerRows.filter(isMainTransactionDetailRow);

      // Reversed trade: if only REV# mirror lines exist, rebuild main table from them
      if (details.length === 0 && finalizeReversalRows.length > 0) {
        suppressFinalizeReversalTable = true;
        details = finalizeReversalRows.map((row) => ({
          accountNumber: row.accountNumber,
          accountName: row.accountName,
          debit: parseFloat(row.credit) || 0,
          credit: parseFloat(row.debit) || 0,
          description: row.description,
          date: row.date || row.chequeDate || finalizedDate || todayStr,
        }));
      } else if (details.length === 0 && tradeLedgerRows.length === 0) {
        details = getTransactionDetails();
      }
    } else {
      // Show preview of what will be posted

      // Note: For We Hold = Yes, blue section (first 9 transactions) uses finalized date,

      // and red section (last 3 transactions) uses closing date

      details = getTransactionDetails();

      // When Confirm Payment popup is open (We Hold = No), include the 2 payment transactions in print
      if (
        getWeHoldValue() === "No" &&
        showPaymentPopup &&
        parseFloat(paymentAmount) > 0 &&
        receivedFrom.trim()
      ) {
        const paymentDesc = `Trade #: ${trade.tradeNumber} - ${getTradeAddress()} - Received From: ${receivedFrom}`;
        const popupAmt = parseFloat(paymentAmount) || 0;
        const payDate = closingDate || todayStr;
        details = [
          ...details,
          {
            accountNumber: "10004",
            accountName: "Cash - Commission Trust Account",
            debit: popupAmt,
            credit: 0,
            description: paymentDesc,
            date: payDate,
          },
          {
            accountNumber: "12200",
            accountName: "A/R - Commission From Deals",
            debit: 0,
            credit: popupAmt,
            description: paymentDesc,
            date: payDate,
          },
        ];
      }
    }

    // Calculate closing balance for Transaction Details

    let totalDebit = 0;

    let totalCredit = 0;

    details.forEach((row) => {
      totalDebit += Number(row.debit) || 0;

      totalCredit += Number(row.credit) || 0;
    });

    // Absorb penny round-off in display: adjust a 21300 entry so totals balance
    const netDiff = roundTo2(totalCredit - totalDebit);
    if (Math.abs(netDiff) > 0 && Math.abs(netDiff) < 0.02) {
      const adjAmt = Math.abs(netDiff);
      if (netDiff < 0) {
        // Need more credit - add to a 21300 credit row
        const adjRow = details.find(
          (r) =>
            r.accountNumber === "21300" &&
            (parseFloat(r.credit) || 0) >= adjAmt,
        );
        if (adjRow) {
          adjRow.credit = roundTo2((parseFloat(adjRow.credit) || 0) + adjAmt);
          totalCredit = roundTo2(totalCredit + adjAmt);
        }
      } else {
        // Need more debit - add to a 21300 debit row
        const adjRow = details.find(
          (r) =>
            r.accountNumber === "21300" && (parseFloat(r.debit) || 0) >= adjAmt,
        );
        if (adjRow) {
          adjRow.debit = roundTo2((parseFloat(adjRow.debit) || 0) + adjAmt);
          totalDebit = roundTo2(totalDebit + adjAmt);
        }
      }
    }

    const closingBalance = roundTo2(totalCredit - totalDebit).toFixed(2);

    const sumLedgerRows = (rows) => {
      let debit = 0;
      let credit = 0;
      rows.forEach((row) => {
        debit += Number(row.debit) || 0;
        credit += Number(row.credit) || 0;
      });
      return {
        debit,
        credit,
        net: roundTo2(credit - debit).toFixed(2),
      };
    };

    const buildPrintLedgerTableRows = (rows, includeReference = false) =>
      rows
        .map(
          (row) => `
        <tr>
          <td style="border: 1px solid #000; padding: 8px; min-width: 120px; width: 120px;">${formatDate(
            row.date || row.chequeDate || finalizedDate || todayStr,
          )}</td>
          <td style="border: 1px solid #000; padding: 8px;">${
            row.accountNumber || ""
          }</td>
          <td style="border: 1px solid #000; padding: 8px;">${
            row.accountName || ""
          }</td>
          <td style="border: 1px solid #000; padding: 8px; text-align: right;">${
            row.debit ? Number(row.debit).toFixed(2) : ""
          }</td>
          <td style="border: 1px solid #000; padding: 8px; text-align: right;">${
            row.credit ? Number(row.credit).toFixed(2) : ""
          }</td>
          <td style="border: 1px solid #000; padding: 8px;">${
            row.description || ""
          }</td>
          ${
            includeReference
              ? `<td style="border: 1px solid #000; padding: 8px;">${
                  row.eftNumber
                    ? String(row.eftNumber).match(/^\d+$/)
                      ? `EFT#${row.eftNumber}`
                      : row.eftNumber
                    : ""
                }</td>`
              : ""
          }
        </tr>
      `,
        )
        .join("");

    const finalizeReversalTotals = sumLedgerRows(finalizeReversalRows);
    const finalizeReversalTableRows = buildPrintLedgerTableRows(
      finalizeReversalRows,
      true,
    );

    const commissionReversalTotals = sumLedgerRows(commissionReversalRows);
    const commissionReversalTableRows = buildPrintLedgerTableRows(
      commissionReversalRows,
      true,
    );

    const postFinalizeTotals = sumLedgerRows(postFinalizeActivityRows);
    const postFinalizeTableRows = buildPrintLedgerTableRows(
      postFinalizeActivityRows,
      true,
    );

    // Filter trust deposit transactions for the current trade (dedupe accidental double-posts)

    const trustDepositRowsFiltered = dedupeTrustDepositLedgerRows(
      ledgerEntries.filter(
        (row) =>
          (row.accountNumber === "10002" || row.accountNumber === "21300") &&
          row.description &&
          row.description.includes(`Trade #: ${trade.tradeNumber}`) &&
          row.description.toLowerCase().includes("received from") &&
          // If a trust-deposit reversal (or any REV reference) exists, it will be shown in reversal tables
          !/^rev/i.test(String(row.eftNumber || "").trim()) &&
          !/reversal/i.test(String(row.description || "")),
      ),
    );

    const trustDepositRows = trustDepositRowsFiltered

      .map(
        (row) => `

        <tr>

          <td style="border: 1px solid #000; padding: 8px; min-width: 120px; width: 120px;">${formatDate(
            row.date || finalizedDate || todayStr,
          )}</td>

          <td style="border: 1px solid #000; padding: 8px;">${
            row.accountNumber || ""
          }</td>

          <td style="border: 1px solid #000; padding: 8px;">${
            row.accountName || ""
          }</td>

          <td style="border: 1px solid #000; padding: 8px; text-align: right;">${
            row.debit ? Number(row.debit).toFixed(2) : ""
          }</td>

          <td style="border: 1px solid #000; padding: 8px; text-align: right;">${
            row.credit ? Number(row.credit).toFixed(2) : ""
          }</td>

          <td style="border: 1px solid #000; padding: 8px;">${
            row.description || ""
          }</td>

        </tr>

      `,
      )

      .join("");

    let trustDepositDebit = 0;

    let trustDepositCredit = 0;

    trustDepositRowsFiltered.forEach((row) => {
      trustDepositDebit += Number(row.debit) || 0;

      trustDepositCredit += Number(row.credit) || 0;
    });

    const trustDepositClosing = (
      trustDepositCredit - trustDepositDebit
    ).toFixed(2);

    // Commission Trust Payments: show ledger entries for each EFT

    let commissionTrustDebit = 0;

    let commissionTrustCredit = 0;

    const commissionTrustRows = commissionTrustTransactions

      .map((eft) => {
        const entries = getLedgerEntriesForEFT(eft.eftNumber);

        if (!entries.length) return "";

        return entries

          .map((row) => {
            commissionTrustDebit += Number(row.debit) || 0;

            commissionTrustCredit += Number(row.credit) || 0;

            // Use the date from the ledger entry (trial balance) instead of EFT date

            const transactionDate =
              row.chequeDate ||
              row.date ||
              eft.date ||
              finalizedDate ||
              todayStr;

            return `

          <tr>

            <td style="border: 1px solid #000; padding: 8px; min-width: 120px; width: 120px;">${formatDate(
              transactionDate,
            )}</td>

            <td style="border: 1px solid #000; padding: 8px;">${
              row.accountNumber || ""
            }</td>

            <td style="border: 1px solid #000; padding: 8px;">${
              row.accountName || ""
            }</td>

            <td style="border: 1px solid #000; padding: 8px; text-align: right;">${
              row.debit ? Number(row.debit).toFixed(2) : ""
            }</td>

            <td style="border: 1px solid #000; padding: 8px; text-align: right;">${
              row.credit ? Number(row.credit).toFixed(2) : ""
            }</td>

            <td style="border: 1px solid #000; padding: 8px;">${
              row.description || ""
            }</td>

            <td style="border: 1px solid #000; padding: 8px;">${
              eft.eftNumber || ""
            }</td>

          </tr>

        `;
          })

          .join("");
      })

      .join("");

    const commissionTrustClosing = (
      commissionTrustCredit - commissionTrustDebit
    ).toFixed(2);

    // Real Estate Trust Payments: show ledger entries for each EFT

    let realEstateTrustDebit = 0;

    let realEstateTrustCredit = 0;

    const realEstateTrustRows = realEstateTrustTransactions

      .map((eft) => {
        const entries = getLedgerEntriesForEFT(eft.eftNumber).filter(
          (row) =>
            row.description &&
            row.description.includes(`Trade #: ${trade.tradeNumber}`),
        );

        const eftDate = eft.date ? eft.date : finalizedDate || todayStr;

        // If this EFT is a Commission Transfer, generate 10004/10002 rows

        if (eft.type === "CommissionTransfer") {
          // Add to totals

          if (eft.amount) {
            realEstateTrustDebit += Number(eft.amount) || 0;

            realEstateTrustCredit += Number(eft.amount) || 0;
          }

          return [
            `<tr>

              <td style="border: 1px solid #000; padding: 8px; min-width: 120px; width: 120px;">${formatDate(
                eftDate,
              )}</td>

              <td style="border: 1px solid #000; padding: 8px;">10004</td>

              <td style="border: 1px solid #000; padding: 8px;">CASH - COMMISSION TRUST ACCOUNT</td>

              <td style="border: 1px solid #000; padding: 8px; text-align: right;">${
                eft.amount ? Number(eft.amount).toFixed(2) : ""
              }</td>

              <td style="border: 1px solid #000; padding: 8px; text-align: right;"></td>

              <td style="border: 1px solid #000; padding: 8px;">Commission Transfer for Trade #: ${
                trade.tradeNumber
              }</td>

              <td style="border: 1px solid #000; padding: 8px;">${
                eft.eftNumber || ""
              }</td>

            </tr>`,

            `<tr>

              <td style="border: 1px solid #000; padding: 8px; min-width: 120px; width: 120px;">${formatDate(
                eftDate,
              )}</td>

              <td style="border: 1px solid #000; padding: 8px;">10002</td>

              <td style="border: 1px solid #000; padding: 8px;">CASH - TRUST</td>

              <td style="border: 1px solid #000; padding: 8px; text-align: right;"></td>

              <td style="border: 1px solid #000; padding: 8px; text-align: right;">${
                eft.amount ? Number(eft.amount).toFixed(2) : ""
              }</td>

              <td style="border: 1px solid #000; padding: 8px;">Commission Transfer for Trade #: ${
                trade.tradeNumber
              }</td>

              <td style="border: 1px solid #000; padding: 8px;">${
                eft.eftNumber || ""
              }</td>

            </tr>`,
          ].join("");
        }

        // Otherwise, render as before (Balance of Deposit, etc)

        entries.forEach((row) => {
          realEstateTrustDebit += Number(row.debit) || 0;

          realEstateTrustCredit += Number(row.credit) || 0;
        });

        return entries

          .map((row) => {
            // Use the date from the ledger entry (trial balance) instead of EFT date

            const transactionDate = row.chequeDate || row.date || eftDate;

            return `

          <tr>

            <td style="border: 1px solid #000; padding: 8px; min-width: 120px; width: 120px;">${formatDate(
              transactionDate,
            )}</td>

            <td style="border: 1px solid #000; padding: 8px;">${
              row.accountNumber || ""
            }</td>

            <td style="border: 1px solid #000; padding: 8px;">${
              row.accountName || ""
            }</td>

            <td style="border: 1px solid #000; padding: 8px; text-align: right;">${
              row.debit ? Number(row.debit).toFixed(2) : ""
            }</td>

            <td style="border: 1px solid #000; padding: 8px; text-align: right;">${
              row.credit ? Number(row.credit).toFixed(2) : ""
            }</td>

            <td style="border: 1px solid #000; padding: 8px;">${
              row.description || ""
            }</td>

            <td style="border: 1px solid #000; padding: 8px;">${
              eft.eftNumber || ""
            }</td>

          </tr>

        `;
          })

          .join("");
      })

      .join("");

    const realEstateTrustClosing = (
      realEstateTrustCredit - realEstateTrustDebit
    ).toFixed(2);

    // Get address details for header

    const k = trade?.keyInfo || {};

    const address = `${k.streetNumber || ""} ${k.streetName || ""}`.trim();

    const postal = k.postalCode || "";

    const city = k.city || "";

    const province = k.province || "";

    const printWindow = window.open("", "", "width=900,height=700");

    const html = `

      <html>

      <head>

        <title>Transaction Details</title>

        <style>

          body { font-family: Arial, sans-serif; margin: 40px; font-size: 13px; }

          h2 { margin-bottom: 20px; }

          h3 { margin-top: 30px; margin-bottom: 10px; font-size: 15px; }

          .header { margin-bottom: 30px; }

          .header div { margin-bottom: 2px; }

          table { border-collapse: collapse; width: 100%; margin-bottom: 20px; font-size: 12px; }

          th, td { border: 1px solid #ccc; padding: 6px 10px; text-align: left; }

          th { background: #f5f5f5; }

          @media print { .no-print { display: none !important; } }

        </style>

      </head>

      <body>

        <div class="header">

          <div><b>ABC Realty Inc., Brokerage</b></div>

          <div>Trade #: ${trade.tradeNumber}</div>

          <div>${address}${
            address && (city || province || postal) ? "," : ""
          } ${city}${city && province ? "," : ""} ${province} ${postal}</div>

        </div>

        <h2>Transaction Details for Trade #${trade.tradeNumber}</h2>

        <table style="width: 100%; border-collapse: collapse;">

          <thead>

            <tr>

              <th style="border: 1px solid #000; padding: 8px; text-align: left; min-width: 120px; width: 120px;">Date</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Number</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Name</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Debit</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Credit</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Description</th>

            </tr>

          </thead>

          <tbody>

            ${details

              .map((row, index) => {
                return `

                  <tr>

                    <td style="border: 1px solid #000; padding: 8px; min-width: 120px; width: 120px;">${
                      row.date || finalizedDate || todayStr
                    }</td>

                    <td style="border: 1px solid #000; padding: 8px;">${
                      row.accountNumber
                    }</td>

                    <td style="border: 1px solid #000; padding: 8px;">${
                      row.accountName
                    }</td>

                    <td style="border: 1px solid #000; padding: 8px; text-align: right;">${
                      row.debit !== 0 ? row.debit.toFixed(2) : ""
                    }</td>

                    <td style="border: 1px solid #000; padding: 8px; text-align: right;">${
                      row.credit !== 0 ? row.credit.toFixed(2) : ""
                    }</td>

                    <td style="border: 1px solid #000; padding: 8px;">${
                      row.description
                    }</td>

                  </tr>

                `;
              })

              .join("")}

          </tbody>

          <tfoot>

            <tr>

              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Total</b></td>

              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${totalDebit.toFixed(
                2,
              )}</b></td>

              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${totalCredit.toFixed(
                2,
              )}</b></td>

              <td style="border: 1px solid #000; padding: 8px;"></td>

            </tr>

            <tr>

              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Net (Credit - Debit)</b></td>

              <td colspan="3" style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${closingBalance}</b></td>

            </tr>

          </tfoot>

        </table>

        ${
          postFinalizeTableRows
            ? `<h3>Builder Repayment, General to Commission Transfer &amp; Commission Recall</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr>
              <th style="border: 1px solid #000; padding: 8px; text-align: left; min-width: 120px; width: 120px;">Date</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Number</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Name</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Debit</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Credit</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Description</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Reference</th>
            </tr>
          </thead>
          <tbody>
            ${postFinalizeTableRows}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Total</b></td>
              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${postFinalizeTotals.debit.toFixed(
                2,
              )}</b></td>
              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${postFinalizeTotals.credit.toFixed(
                2,
              )}</b></td>
              <td colspan="2" style="border: 1px solid #000; padding: 8px;"></td>
            </tr>
            <tr>
              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Net (Credit - Debit)</b></td>
              <td colspan="4" style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${postFinalizeTotals.net}</b></td>
            </tr>
          </tfoot>
        </table>`
            : ""
        }

        ${
          commissionReversalTableRows
            ? `<h3>Commission Reversals</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr>
              <th style="border: 1px solid #000; padding: 8px; text-align: left; min-width: 120px; width: 120px;">Date</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Number</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Name</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Debit</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Credit</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Description</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Reference</th>
            </tr>
          </thead>
          <tbody>
            ${commissionReversalTableRows}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Total</b></td>
              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${commissionReversalTotals.debit.toFixed(
                2,
              )}</b></td>
              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${commissionReversalTotals.credit.toFixed(
                2,
              )}</b></td>
              <td colspan="2" style="border: 1px solid #000; padding: 8px;"></td>
            </tr>
            <tr>
              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Net (Credit - Debit)</b></td>
              <td colspan="4" style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${commissionReversalTotals.net}</b></td>
            </tr>
          </tfoot>
        </table>`
            : ""
        }

        ${
          finalizeReversalTableRows && !suppressFinalizeReversalTable
            ? `<h3>Reversal Transactions</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr>
              <th style="border: 1px solid #000; padding: 8px; text-align: left; min-width: 120px; width: 120px;">Date</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Number</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Name</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Debit</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Credit</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Description</th>
              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Reference</th>
            </tr>
          </thead>
          <tbody>
            ${finalizeReversalTableRows}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Total</b></td>
              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${finalizeReversalTotals.debit.toFixed(
                2,
              )}</b></td>
              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${finalizeReversalTotals.credit.toFixed(
                2,
              )}</b></td>
              <td colspan="2" style="border: 1px solid #000; padding: 8px;"></td>
            </tr>
            <tr>
              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Net (Credit - Debit)</b></td>
              <td colspan="4" style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${finalizeReversalTotals.net}</b></td>
            </tr>
          </tfoot>
        </table>`
            : ""
        }

        ${
          trustDepositRows || commissionTrustRows || realEstateTrustRows
            ? `<div style="page-break-inside: avoid;">

                ${
                  trustDepositRows
                    ? `<h3>Trust Deposit Transactions</h3>

        <table style="width: 100%; border-collapse: collapse;">

          <thead>

            <tr>

              <th style="border: 1px solid #000; padding: 8px; text-align: left; min-width: 120px; width: 120px;">Date</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Number</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Name</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Debit</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Credit</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Description</th>

            </tr>

          </thead>

          <tbody>

            ${trustDepositRows}

          </tbody>

          <tfoot>

            <tr>

              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Total</b></td>

              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${trustDepositDebit.toFixed(
                2,
              )}</b></td>

              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${trustDepositCredit.toFixed(
                2,
              )}</b></td>

              <td style="border: 1px solid #000; padding: 8px;"></td>

            </tr>

            <tr>

              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Net (Credit - Debit)</b></td>

              <td colspan="3" style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${trustDepositClosing}</b></td>

            </tr>

          </tfoot>

        </table>`
                    : ""
                }

                ${
                  commissionTrustRows
                    ? `<h3>Commission Trust Payments</h3>

        <table style="width: 100%; border-collapse: collapse;">

          <thead>

            <tr>

              <th style="border: 1px solid #000; padding: 8px; text-align: left; min-width: 120px; width: 120px;">Date</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Number</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Name</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Debit</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Credit</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Description</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">EFT #</th>

            </tr>

          </thead>

          <tbody>

            ${commissionTrustRows}

          </tbody>

          <tfoot>

            <tr>

              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Total</b></td>

              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${commissionTrustDebit.toFixed(
                2,
              )}</b></td>

              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${commissionTrustCredit.toFixed(
                2,
              )}</b></td>

              <td colspan="2" style="border: 1px solid #000; padding: 8px;"></td>

            </tr>

            <tr>

              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Net (Credit - Debit)</b></td>

              <td colspan="4" style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${commissionTrustClosing}</b></td>

            </tr>

          </tfoot>

        </table>`
                    : ""
                }

                ${
                  realEstateTrustRows
                    ? `<h3>Real Estate Trust Payments</h3>

        <table style="width: 100%; border-collapse: collapse;">

          <thead>

            <tr>

              <th style="border: 1px solid #000; padding: 8px; text-align: left; min-width: 120px; width: 120px;">Date</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Number</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Account Name</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Debit</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: right;">Credit</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">Description</th>

              <th style="border: 1px solid #000; padding: 8px; text-align: left;">EFT #</th>

            </tr>

          </thead>

          <tbody>

            ${realEstateTrustRows}

          </tbody>

          <tfoot>

            <tr>

              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Total</b></td>

              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${realEstateTrustDebit.toFixed(
                2,
              )}</b></td>

              <td style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${realEstateTrustCredit.toFixed(
                2,
              )}</b></td>

              <td colspan="2" style="border: 1px solid #000; padding: 8px;"></td>

            </tr>

            <tr>

              <td colspan="3" style="border: 1px solid #000; padding: 8px;"><b>Net (Credit - Debit)</b></td>

              <td colspan="4" style="border: 1px solid #000; padding: 8px; text-align: right;"><b>${realEstateTrustClosing}</b></td>

            </tr>

          </tfoot>

        </table>`
                    : ""
                }

              </div>`
            : ""
        }

        <button onclick="window.print()" class="no-print" style="padding:10px 20px;font-size:16px;">Print</button>

      </body>

      </html>

    `;

    printWindow.document.write(html);

    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
        <button
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-2xl"
          onClick={() => {
            setEftNumber(""); // Reset EFT number when closing modal

            if (onClose) onClose();
          }}
        >
          &times;
        </button>

        <h2 className="text-xl font-bold mb-4">Finalize Trade</h2>

        {getWeHoldValue() === "Yes" ? (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-medium mb-1">Finalized Date</label>

              <input
                type="date"
                className="border rounded px-2 py-1 w-full"
                value={finalizedDate}
                onChange={(e) => setFinalizedDate(e.target.value)}
              />
            </div>

            <div>
              <label className="block font-medium mb-1">Closing Date</label>

              <div
                className={`border rounded px-2 py-1 ${
                  !closingDate && getWeHoldValue() === "Yes"
                    ? "bg-red-50 border-red-300"
                    : "bg-gray-50"
                }`}
              >
                {formatDateToMMDDYYYY(closingDate)}
              </div>

              {!closingDate && getWeHoldValue() === "Yes" && (
                <p className="text-red-500 text-xs mt-1">
                  Closing date is required when We Hold = Yes
                </p>
              )}
            </div>

            <div>
              <label className="block font-medium mb-1">Deposit Amount</label>

              <div className="border rounded px-2 py-1 bg-gray-50">
                {getDepositAmount()}
              </div>
            </div>

            <div>
              <label className="block font-medium mb-1">We Hold</label>

              <div className="border rounded px-2 py-1 bg-gray-50">
                {getWeHoldValue()}
              </div>
            </div>

            {showDualSideLayout() ? (
              <>
                {getAgentsWithListingSellingDetails().map(
                  (agentGroup, agentIdx) => (
                    <React.Fragment key={agentIdx}>
                      <div className="col-span-2">
                        <h3 className="text-sm font-semibold text-gray-800 mb-2 mt-4 first:mt-2">
                          {agentGroup.agentName}
                        </h3>
                      </div>
                      {agentGroup.listingRow && (
                        <>
                          <div className="col-span-2">
                            <h4 className="text-xs font-semibold text-gray-600 mb-1">
                              Listing side details
                            </h4>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Listing side Commission
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.listingRow.amount || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Listing side Net Commission
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.listingRow.netCommission || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Listing side HST Amount
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.listingRow.tax || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Listing side Fees Deducted
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.listingRow.feesDeducted || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Listing side Tax on Fees Deducted
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.listingRow.feesDeducted
                                ? (
                                    parseFloat(
                                      agentGroup.listingRow.feesDeducted,
                                    ) * 0.13
                                  ).toFixed(2)
                                : "-"}
                            </div>
                          </div>
                        </>
                      )}
                      {agentGroup.sellingRow && (
                        <>
                          <div className="col-span-2">
                            <h4 className="text-xs font-semibold text-gray-600 mb-1 mt-2">
                              Selling side details
                            </h4>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Selling side Commission
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.sellingRow.amount || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Selling side Net Commission
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.sellingRow.netCommission || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Selling side HST Amount
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.sellingRow.tax || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Selling side Fees Deducted
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.sellingRow.feesDeducted || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Selling side Tax on Fees Deducted
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.sellingRow.feesDeducted
                                ? (
                                    parseFloat(
                                      agentGroup.sellingRow.feesDeducted,
                                    ) * 0.13
                                  ).toFixed(2)
                                : "-"}
                            </div>
                          </div>
                        </>
                      )}
                    </React.Fragment>
                  ),
                )}
                <div className="col-span-2">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-4">
                    Commission totals
                  </h3>
                </div>
                <div>
                  <label className="block font-medium mb-1">
                    Listing Amount
                  </label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {commissionData.listing?.listingAmount || "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">Listing Tax</label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {commissionData.listing?.listingTax || "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Selling Amount
                  </label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {commissionData.selling?.sellingAmount || "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">Selling Tax</label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {commissionData.selling?.sellingTax || "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Total Commission
                  </label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {getTotalCommission()}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block font-medium mb-1">
                    Agent Commission
                  </label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {agentData.amount || "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">Agent Net</label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {agentData.netCommission || "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">Agent HST</label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {agentData.tax || "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Listing Amount
                  </label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {commissionData.listing?.listingAmount || "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">Listing Tax</label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {commissionData.listing?.listingTax || "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Selling Amount
                  </label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {commissionData.selling?.sellingAmount || "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">Selling Tax</label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {commissionData.selling?.sellingTax || "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Total Commission
                  </label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {getTotalCommission()}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Fees Deducted
                  </label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {agentData.feesDeducted || "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Tax on Fees Deducted
                  </label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {agentData.totalFees && agentData.feesDeducted
                      ? (
                          parseFloat(agentData.totalFees) -
                          parseFloat(agentData.feesDeducted)
                        ).toFixed(2)
                      : "-"}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">Buyer Rebate</label>

                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {getTotalBuyerRebate()}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-medium mb-1">Finalized Date</label>

              <input
                type="date"
                className="border rounded px-2 py-1 w-full"
                value={finalizedDate}
                onChange={(e) => setFinalizedDate(e.target.value)}
              />
            </div>

            <div>
              <label className="block font-medium mb-1">Closing Date</label>

              <input
                type="date"
                className="border rounded px-2 py-1 w-full"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
              />
            </div>

            <div>
              <label className="block font-medium mb-1">End</label>

              <select
                className="border rounded px-2 py-1 w-full"
                value={getWeHoldValue() === "No" ? "Selling Side" : end}
                onChange={(e) => setEnd(e.target.value)}
                disabled={getWeHoldValue() === "No"}
              >
                <option value="">Select</option>

                <option value="Listing Side">Listing Side</option>

                <option value="Selling Side">Selling Side</option>
              </select>
            </div>

            <div>
              <label className="block font-medium mb-1">Deposit Amount</label>

              <div className="border rounded px-2 py-1 bg-gray-50">
                {getDepositAmount()}
              </div>
            </div>

            {/* We Hold field (read-only, auto-filled) */}

            <div>
              <label className="block font-medium mb-1">We Hold</label>

              <div className="border rounded px-2 py-1 bg-gray-50">
                {getWeHoldValue()}
              </div>
            </div>

            <div>
              <label className="block font-medium mb-1">Commission</label>

              <div className="border rounded px-2 py-1 bg-gray-50">
                {commission}
              </div>
            </div>

            <div>
              <label className="block font-medium mb-1">Tax</label>

              <div className="border rounded px-2 py-1 bg-gray-50">{tax}</div>
            </div>

            <div>
              <label className="block font-medium mb-1">Total</label>

              <div className="border rounded px-2 py-1 bg-gray-50">{total}</div>
            </div>

            <div>
              <label className="block font-medium mb-1">Total Commission</label>

              <div className="border rounded px-2 py-1 bg-gray-50">
                {getTotalCommission()}
              </div>
            </div>

            {showDualSideLayout() ? (
              <>
                {getAgentsWithListingSellingDetails().map(
                  (agentGroup, agentIdx) => (
                    <React.Fragment key={agentIdx}>
                      <div className="col-span-2">
                        <h3 className="text-sm font-semibold text-gray-800 mb-2 mt-4 first:mt-2">
                          {agentGroup.agentName}
                        </h3>
                      </div>
                      {agentGroup.listingRow && (
                        <>
                          <div className="col-span-2">
                            <h4 className="text-xs font-semibold text-gray-600 mb-1">
                              Listing side details
                            </h4>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Listing side Commission
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.listingRow.amount || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Listing side Net
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.listingRow.netCommission || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Listing side Fees Deducted
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.listingRow.feesDeducted || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Listing side Tax for Deduction
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.listingRow.feesDeducted
                                ? (
                                    parseFloat(
                                      agentGroup.listingRow.feesDeducted,
                                    ) * 0.13
                                  ).toFixed(2)
                                : "-"}
                            </div>
                          </div>
                        </>
                      )}
                      {agentGroup.sellingRow && (
                        <>
                          <div className="col-span-2">
                            <h4 className="text-xs font-semibold text-gray-600 mb-1 mt-2">
                              Selling side details
                            </h4>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Selling side Commission
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.sellingRow.amount || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Selling side Net
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.sellingRow.netCommission || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Selling side Fees Deducted
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.sellingRow.feesDeducted || "-"}
                            </div>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Selling side Tax for Deduction
                            </label>
                            <div className="border rounded px-2 py-1 bg-gray-50">
                              {agentGroup.sellingRow.feesDeducted
                                ? (
                                    parseFloat(
                                      agentGroup.sellingRow.feesDeducted,
                                    ) * 0.13
                                  ).toFixed(2)
                                : "-"}
                            </div>
                          </div>
                        </>
                      )}
                    </React.Fragment>
                  ),
                )}
              </>
            ) : (
              <>
                <div className="col-span-2">
                  <label className="block font-medium mb-1">
                    Agent Details
                  </label>
                  <div className="border rounded px-3 py-3 bg-gray-50 space-y-3 text-sm">
                    {/* Referral agent(s) */}
                    {(agentList || [])
                      .filter(
                        (a) =>
                          (a.classification || "").toUpperCase() === "REFERRAL",
                      )
                      .map((agent, idx) => {
                        const amount = agent.amount || "-";
                        const net = agent.netCommission || "-";
                        const fees = agent.feesDeducted || "0.00";
                        const taxForDeduction =
                          agent.totalFees && agent.feesDeducted
                            ? (
                                parseFloat(agent.totalFees) -
                                parseFloat(agent.feesDeducted)
                              ).toFixed(2)
                            : "0.00";
                        return (
                          <div
                            key={`ref-${idx}`}
                            className="border-b pb-2 mb-2 last:border-b-0 last:pb-0 last:mb-0"
                          >
                            <div className="font-semibold mb-1">
                              Referral Agent: {agent.agentName || "-"}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="font-medium">
                                  Commission:{" "}
                                </span>
                                <span>{amount}</span>
                              </div>
                              <div>
                                <span className="font-medium">Net: </span>
                                <span>{net}</span>
                              </div>
                              <div>
                                <span className="font-medium">
                                  Fees Deducted:{" "}
                                </span>
                                <span>{fees}</span>
                              </div>
                              <div>
                                <span className="font-medium">
                                  Tax For Deduction:{" "}
                                </span>
                                <span>{taxForDeduction}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                    {/* Non-referral agents */}
                    {(agentList || [])
                      .filter(
                        (a) =>
                          (a.classification || "").toUpperCase() !== "REFERRAL",
                      )
                      .map((agent, idx) => {
                        const amount = agent.amount || "-";
                        const net = agent.netCommission || "-";
                        const fees = agent.feesDeducted || "0.00";
                        const taxForDeduction =
                          agent.totalFees && agent.feesDeducted
                            ? (
                                parseFloat(agent.totalFees) -
                                parseFloat(agent.feesDeducted)
                              ).toFixed(2)
                            : "0.00";
                        return (
                          <div
                            key={`agent-${idx}`}
                            className="border-b pb-2 mb-2 last:border-b-0 last:pb-0 last:mb-0"
                          >
                            <div className="font-semibold mb-1">
                              Agent {idx + 1}: {agent.agentName || "-"}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="font-medium">
                                  Commission:{" "}
                                </span>
                                <span>{amount}</span>
                              </div>
                              <div>
                                <span className="font-medium">Net: </span>
                                <span>{net}</span>
                              </div>
                              <div>
                                <span className="font-medium">
                                  Fees Deducted:{" "}
                                </span>
                                <span>{fees}</span>
                              </div>
                              <div>
                                <span className="font-medium">
                                  Tax For Deduction:{" "}
                                </span>
                                <span>{taxForDeduction}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="fallenThru"
              checked={fallenThru}
              onChange={(e) => setFallenThru(e.target.checked)}
              className="mr-2"
            />

            <label htmlFor="fallenThru" className="font-medium">
              Fallen Thru
            </label>
          </div>

          <div className="flex gap-2">
            <button
              className="px-6 py-2 bg-blue-900 text-white font-semibold rounded hover:bg-blue-800 disabled:opacity-60"
              onClick={handlePrintDetails}
              disabled={isFinalizing}
            >
              Print Details
            </button>

            <button
              className="px-6 py-2 bg-blue-900 text-white font-semibold rounded hover:bg-blue-800 disabled:opacity-60"
              onClick={handleFinalizeTrade}
              disabled={isFinalizing}
            >
              {isFinalizing ? "Finalizing..." : "Finalize Trade"}
            </button>
          </div>
        </div>

        {/* Payment Confirmation Popup */}

        {showPaymentPopup && (
          <div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={() => {
              setShowPaymentPopup(false);
            }}
          >
            <div
              className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md relative"
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
            >
              <button
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-2xl"
                onClick={() => setShowPaymentPopup(false)}
              >
                &times;
              </button>

              <h2 className="text-lg font-bold mb-4">Confirm Payment</h2>

              <div className="mb-4">
                <label className="block font-medium mb-1">Finalized Date</label>

                <input
                  type="date"
                  className="border rounded px-2 py-1 w-full"
                  value={finalizedDate}
                  onChange={(e) => setFinalizedDate(e.target.value)}
                />
              </div>

              {getWeHoldValue() === "Yes" && (
                <div className="mb-4">
                  <label className="block font-medium mb-1">Closing Date</label>

                  <div
                    className={`border rounded px-2 py-1 ${
                      !closingDate ? "bg-red-50 border-red-300" : "bg-gray-50"
                    }`}
                  >
                    {formatDateToMMDDYYYY(closingDate)}
                  </div>

                  {!closingDate && (
                    <p className="text-red-500 text-xs mt-1">
                      Closing date is required when We Hold = Yes
                    </p>
                  )}
                </div>
              )}

              <div className="mb-4">
                <label className="block font-medium mb-1">Received From</label>

                <input
                  type="text"
                  className="border rounded px-2 py-1 w-full"
                  value={receivedFrom}
                  onChange={(e) => setReceivedFrom(e.target.value)}
                />
              </div>

              <div className="mb-4">
                <label className="block font-medium mb-1">Amount</label>

                <input
                  type="number"
                  className="border rounded px-2 py-1 w-full"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  step="0.01"
                />
              </div>

              <div className="mb-4">
                <label className="block font-medium mb-1">Payment Type</label>

                <select
                  className="border rounded px-2 py-1 w-full"
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                >
                  {paymentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* EFT# field - only show when deposit amount is less than total commission */}

              {(() => {
                const depositAmount = parseFloat(getDepositAmount()) || 0;

                const totalCommission = parseFloat(getTotalCommission()) || 0;

                return totalCommission > depositAmount ? (
                  <div className="mb-4">
                    <label className="block font-medium mb-1">EFT#</label>

                    <input
                      type="text"
                      className="border rounded px-2 py-1 w-full bg-gray-50"
                      value={eftNumber}
                      readOnly
                    />
                  </div>
                ) : null;
              })()}

              <div className="flex justify-end gap-2">
                <button
                  className="px-6 py-2 bg-gray-500 text-white font-semibold rounded hover:bg-gray-600 disabled:opacity-60"
                  onClick={() => setShowPaymentPopup(false)}
                  disabled={isFinalizing}
                >
                  Cancel
                </button>

                <button
                  className="px-6 py-2 bg-blue-900 text-white font-semibold rounded hover:bg-blue-800 disabled:opacity-60"
                  onClick={handlePrintDetails}
                  disabled={isFinalizing}
                >
                  Print Details
                </button>

                <button
                  className="px-6 py-2 bg-blue-900 text-white font-semibold rounded hover:bg-blue-800 disabled:opacity-60"
                  onClick={handleConfirmPayment}
                  disabled={isFinalizing}
                >
                  {isFinalizing ? "Processing..." : "Confirm Payment"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradeFinalizeModal;
